import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useFeature } from '../../features.js';
import { api, uploadFile, API_ORIGIN } from '../../api.js';
import CustomerFormModal from '../control/CustomerFormModal.jsx';
import LocationFormModal from '../control/LocationFormModal.jsx';
import CustomsBrokerFormModal from '../control/CustomsBrokerFormModal.jsx';
import SlideToggle from '../../components/SlideToggle.jsx';
import './CreateLoad.css';
import PhoneInput from '../../components/PhoneInput.jsx';
import { displayPhone } from '../../phone.js';

const IconFileText = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg>;
const IconDollar = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>;

// Major US/Canada land border crossings for truck freight, as a starting
// point for the Port of Entry dropdown — "+ Add new..." and "Manage" (via
// EditableSelect) extend either list per tenant. CBP port codes included
// where they're well-established; CBSA office codes are left off rather
// than guessed — add them through Manage if your ops team has them on file.
const US_PORTS_OF_ENTRY = [
  'Buffalo–Niagara Falls, NY (0901)',
  'Champlain, NY (0712)',
  'Detroit, MI (3801)',
  'Port Huron, MI (3802)',
  'Pembina, ND (3401)',
  'Sweetgrass, MT (3302)',
  'Blaine, WA (3004)',
  'Sumas, WA (3007)',
  'International Falls, MN (3603)',
  'Massena, NY',
  'Derby Line, VT',
  'Calais, ME',
];
const CA_PORTS_OF_ENTRY = [
  'Lacolle, QC',
  'Fort Erie, ON (Peace Bridge)',
  'Windsor, ON (Ambassador Bridge)',
  'Sarnia, ON (Blue Water Bridge)',
  'Emerson, MB',
  'Coutts, AB',
  'Osoyoos, BC',
  'Huntingdon, BC (Pacific Highway)',
  'St. Stephen, NB',
  'Stanstead, QC',
];

// Common freight commodity types, as a starting point for the Commodity
// dropdown — "+ Add new..." and "Manage" (via EditableSelect) extend it
// per tenant, same as Equipment right next to it.
const COMMON_COMMODITIES = [
  'General Freight',
  'Machinery/Equipment',
  'Building Materials',
  'Food/Beverage',
  'Electronics',
  'Automotive Parts',
  'Paper Products',
  'Textiles/Apparel',
  'Chemicals (Non-Hazmat)',
  'Metals/Steel',
  'Furniture',
  'Plastics',
  'Agricultural Products',
  'Retail/Consumer Goods',
  'Lumber/Wood Products',
];

// A <select> where 'Add new...' and 'Edit selected...' live as entries
// inside the dropdown itself (after a divider), instead of separate side
// buttons — matches native OS dropdown patterns.
function EditableSelect({ value, onChange, baseOptions, customOptions, setCustomOptions, placeholder = 'Select', promptLabel = 'Value', onManage }) {
  const addNew = () => {
    const val = window.prompt(`New ${promptLabel}`);
    if (val && val.trim()) {
      setCustomOptions(list => list.includes(val) ? list : [...list, val]);
      onChange(val);
    }
  };
  const handleChange = (e) => {
    const val = e.target.value;
    if (val === '__add__') { addNew(); return; }
    if (val === '__manage__') { onManage(); return; }
    onChange(val);
  };
  return (
    <select value={value} onChange={handleChange}>
      <option value="">{placeholder}</option>
      {baseOptions.map(o => <option key={o}>{o}</option>)}
      {customOptions.map(o => <option key={o}>{o}</option>)}
      <option disabled>──────────</option>
      <option value="__add__">+ Add new...</option>
      <option value="__manage__">⚙ Manage options...</option>
    </select>
  );
}

// A lookup <select> (Salesman, Location — backed by real API records) with
// 'Add new...' / 'Edit selected...' as in-list entries, same pattern as
// EditableSelect but wired to the shared openAddLookup/openEditLookup flow.
function LookupSelect({ value, onChange, options, kind, context, openAdd, onManage, placeholder = 'Select' }) {
  const handleChange = (e) => {
    const val = e.target.value;
    if (val === '__add__') { openAdd(kind, context); return; }
    if (val === '__manage__') { onManage(kind, context); return; }
    onChange(val);
  };
  return (
    <select value={value} onChange={handleChange}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      <option disabled>──────────</option>
      <option value="__add__">+ Add new...</option>
      <option value="__manage__">⚙ Manage options...</option>
    </select>
  );
}

// Location search — a text input that filters as you type, but selection
// happens from a persistent panel on the LEFT edge of the screen rather
// than an attached dropdown, so it's easy to scan and click without
// hunting for a tiny popover under the input.
function LocationSidePanel({ valueId, valueName, onChange, locations, kind, context, onOpenAddForm }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = query.trim()
    ? locations.filter(l => l.name.toLowerCase().includes(query.trim().toLowerCase()))
    : locations;

  const select = (loc) => {
    onChange(loc.id, loc.name);
    setOpen(false);
    setQuery('');
  };

  const addressLine = (l) => [l.address, l.city, l.province, l.postal_code].filter(Boolean).join(', ');

  return (
    <>
      <input
        value={open ? query : (valueName || '')}
        placeholder="Start typing to search..."
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
      />
      {open && createPortal(
        <>
          <div className="location-panel-backdrop" onClick={() => setOpen(false)} />
          <div className="location-panel">
            <div className="location-panel-header">
              <span>Select Location</span>
              <button type="button" className="modal-close" onClick={() => setOpen(false)}>✕</button>
            </div>
            <div className="location-panel-hint">Type in the Location Name field to filter.</div>
            <div className="location-panel-list">
              {filtered.map(l => (
                <div key={l.id}
                  className={'location-panel-item' + (l.id == valueId ? ' selected' : '')}
                  onClick={() => select(l)}>
                  <div className="location-panel-item-name">
                    {l.name}
                    {l.country && <span className="location-panel-item-badge">{l.country}</span>}
                  </div>
                  {addressLine(l) && <div className="location-panel-item-sub">{addressLine(l)}</div>}
                </div>
              ))}
              {filtered.length === 0 && <div className="location-panel-empty">No locations found.</div>}
            </div>
            <div className="location-panel-footer">
              {query.trim() && (
                <button type="button" className="location-panel-add-link" onClick={() => { onOpenAddForm(query.trim(), context); setOpen(false); }}>
                  + Add New Location "{query.trim()}"
                </button>
              )}
              {!query.trim() && (
                <button type="button" className="location-panel-add-link" onClick={() => { onOpenAddForm('', context); setOpen(false); }}>
                  + Add New Location
                </button>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

// Same side-panel pattern as LocationSidePanel, for Customer. "+ Add New
// Customer" calls onOpenAddForm, which opens Control's CustomerFormModal
// (the one customer form in the app) pre-filled with the typed name.
function CustomerSidePanel({ valueId, valueName, onChange, customers, onOpenAddForm }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = query.trim()
    ? customers.filter(c => c.name.toLowerCase().includes(query.trim().toLowerCase()))
    : customers;

  const select = (c) => {
    onChange(c.id, c.name);
    setOpen(false);
    setQuery('');
  };

  const addressLine = (c) => [c.address, c.city, c.province, c.postal_code].filter(Boolean).join(', ');

  return (
    <>
      <input
        value={open ? query : (valueName || '')}
        placeholder="Start typing to search..."
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
      />
      {open && createPortal(
        <>
          <div className="location-panel-backdrop" onClick={() => setOpen(false)} />
          <div className="location-panel">
            <div className="location-panel-header">
              <span>Select Customer</span>
              <button type="button" className="modal-close" onClick={() => setOpen(false)}>✕</button>
            </div>
            <div className="location-panel-hint">Type in the Customer Name field to filter.</div>
            <div className="location-panel-list">
              {filtered.map(c => (
                <div key={c.id}
                  className={'location-panel-item' + (c.id == valueId ? ' selected' : '')}
                  onClick={() => select(c)}>
                  <div className="location-panel-item-name">
                    {c.name}
                    {c.country && <span className="location-panel-item-badge">{c.country}</span>}
                  </div>
                  {addressLine(c) && <div className="location-panel-item-sub">{addressLine(c)}</div>}
                </div>
              ))}
              {filtered.length === 0 && <div className="location-panel-empty">No customers found.</div>}
            </div>
            <div className="location-panel-footer">
              {query.trim() && (
                <button type="button" className="location-panel-add-link" onClick={() => { onOpenAddForm(query.trim()); setOpen(false); }}>
                  + Add New Customer "{query.trim()}"
                </button>
              )}
              {!query.trim() && (
                <button type="button" className="location-panel-add-link" onClick={() => { onOpenAddForm(''); setOpen(false); }}>
                  + Add New Customer
                </button>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

// Same side-panel pattern again, for Customs Broker — the list handed in
// is already pre-filtered by the border-crossing direction (Outbound shows
// only US brokers, Inbound only Canadian), so this component itself stays
// generic, same as CustomerSidePanel.
function CustomsBrokerSidePanel({ valueId, valueName, onChange, brokers, onOpenAddForm }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = query.trim()
    ? brokers.filter(b => b.name.toLowerCase().includes(query.trim().toLowerCase()))
    : brokers;

  const select = (b) => {
    onChange(b.id, b.name);
    setOpen(false);
    setQuery('');
  };

  const addressLine = (b) => [b.address, b.city, b.province, b.postal_code].filter(Boolean).join(', ');

  return (
    <>
      <input
        value={open ? query : (valueName || '')}
        placeholder="Start typing to search..."
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
      />
      {open && createPortal(
        <>
          <div className="location-panel-backdrop" onClick={() => setOpen(false)} />
          <div className="location-panel">
            <div className="location-panel-header">
              <span>Select Customs Broker</span>
              <button type="button" className="modal-close" onClick={() => setOpen(false)}>✕</button>
            </div>
            <div className="location-panel-hint">Type in the Broker Name field to filter.</div>
            <div className="location-panel-list">
              {filtered.map(b => (
                <div key={b.id}
                  className={'location-panel-item' + (b.id == valueId ? ' selected' : '')}
                  onClick={() => select(b)}>
                  <div className="location-panel-item-name">
                    {b.name}
                    {b.broker_country && <span className="location-panel-item-badge">{b.broker_country}</span>}
                  </div>
                  {addressLine(b) && <div className="location-panel-item-sub">{addressLine(b)}</div>}
                </div>
              ))}
              {filtered.length === 0 && <div className="location-panel-empty">No customs brokers found for this direction.</div>}
            </div>
            <div className="location-panel-footer">
              {query.trim() && (
                <button type="button" className="location-panel-add-link" onClick={() => { onOpenAddForm(query.trim()); setOpen(false); }}>
                  + Add New Customs Broker "{query.trim()}"
                </button>
              )}
              {!query.trim() && (
                <button type="button" className="location-panel-add-link" onClick={() => { onOpenAddForm(''); setOpen(false); }}>
                  + Add New Customs Broker
                </button>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

// Read-only address/contact line shown under a Location field once one is
// picked — same idea as the Customer detail panel, just compact since a
// location doesn't need a credit summary.
function LocationDetailText({ locationId, locations }) {
  const loc = locations.find(l => l.id == locationId);
  if (!loc) return null;
  const addressLine = [loc.address, loc.city, loc.province, loc.postal_code, loc.country].filter(Boolean).join(', ');
  const contactLine = [loc.contact_name, displayPhone(loc.phone)].filter(Boolean).join(' — ');
  if (!addressLine && !contactLine) return null;
  return (
    <div className="location-detail-text">
      {addressLine && <div>{addressLine}</div>}
      {contactLine && <div>{contactLine}</div>}
    </div>
  );
}

function formatTime12h(t) {
  if (!t) return '';
  const [hStr, mStr] = t.split(':');
  let h = parseInt(hStr, 10);
  if (Number.isNaN(h)) return '';
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12; if (h === 0) h = 12;
  return `${String(h).padStart(2, '0')}:${mStr || '00'} ${ampm}`;
}

// Shows a location's Opening/Closing Time next to a PICKUP/DELIVERY header,
// with a pencil icon that swaps in inline time inputs to override the hours
// for just this one stop (the location's own saved hours are never touched).
function StopHoursField({ locationId, locations, overrideStart, overrideEnd, onChangeStart, onChangeEnd }) {
  const [editing, setEditing] = useState(false);
  const loc = locations.find(l => l.id == locationId);
  if (!loc) return null;

  const locStart = loc.opening_time ? String(loc.opening_time).slice(0, 5) : '';
  const locEnd   = loc.closing_time ? String(loc.closing_time).slice(0, 5) : '';
  const effStart = overrideStart || locStart;
  const effEnd   = overrideEnd || locEnd;
  const isOverridden = !!(overrideStart || overrideEnd);

  if (editing) {
    return (
      <span className="stop-hours-field editing">
        <input type="time" value={effStart} onChange={e => onChangeStart(e.target.value)} />
        <span className="stop-hours-sep">–</span>
        <input type="time" value={effEnd} onChange={e => onChangeEnd(e.target.value)} />
        <button type="button" className="stop-hours-edit-btn" title="Done" onClick={() => setEditing(false)}>✓</button>
      </span>
    );
  }

  return (
    <span className="stop-hours-field">
      <span className={'stop-hours-text' + (effStart && effEnd ? '' : ' placeholder')}>
        {effStart && effEnd ? `${formatTime12h(effStart)} – ${formatTime12h(effEnd)}` : 'No hours set'}
      </span>
      {isOverridden && (
        <button type="button" className="stop-hours-reset-btn" title="Reset to location's default hours"
          onClick={() => { onChangeStart(''); onChangeEnd(''); }}>↺</button>
      )}
      <button type="button" className="stop-hours-edit-btn" title="Override hours for this stop"
        onClick={() => setEditing(true)}>✎</button>
    </span>
  );
}

const today = new Date().toISOString().slice(0, 10);

// Card listing every value for one field: locked (built-in/default) entries
// can't be touched, entries the user added themselves can be renamed or
// removed inline, and new ones can be added from the same card.
function ManageOptionsCard({ label, items, onAdd, onEdit, onDelete, onClose }) {
  const [newVal, setNewVal] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editingVal, setEditingVal] = useState('');

  const commitAdd = () => {
    if (!newVal.trim()) return;
    onAdd(newVal.trim());
    setNewVal('');
  };
  const commitEdit = () => {
    if (!editingVal.trim()) return;
    onEdit(editingKey, editingVal.trim());
    setEditingKey(null);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal manage-modal" onClick={e => e.stopPropagation()}>
        <h3>Manage {label}</h3>
        <div className="manage-list">
          {items.map(item => (
            <div className="manage-row" key={item.key}>
              {editingKey === item.key ? (
                <>
                  <input autoFocus value={editingVal} onChange={e => setEditingVal(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && commitEdit()} />
                  <button type="button" className="manage-row-btn" onClick={commitEdit}>Save</button>
                  <button type="button" className="manage-row-btn" onClick={() => setEditingKey(null)}>Cancel</button>
                </>
              ) : (
                <>
                  <span className="manage-row-name">{item.name}</span>
                  {item.locked ? (
                    <span className="manage-row-locked">Default</span>
                  ) : (
                    <>
                      <button type="button" className="manage-row-btn"
                        onClick={() => { setEditingKey(item.key); setEditingVal(item.name); }}>✎</button>
                      <button type="button" className="manage-row-btn manage-row-btn-danger"
                        onClick={() => onDelete(item.key, item.name)}>🗑</button>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
          {items.length === 0 && <div className="combo-empty">Nothing here yet.</div>}
        </div>
        <div className="manage-add-row">
          <input placeholder={`New ${label.toLowerCase()}`} value={newVal}
            onChange={e => setNewVal(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && commitAdd()} />
          <button type="button" className="btn-primary" onClick={commitAdd}>Add</button>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

const blankShipment = () => ({
  commodity: '', valueOfGoods: '', valueCurrency: 'CAD', equipment: '',
  numPackages: '', packageUnit: '', weight: '', weightUnit: 'lbs',
  temperature: '', tempUnit: 'Celsius',
  dimL: '', dimW: '', dimH: '', dimUnit: 'in',
});

const blankStop = (stopType) => ({
  stopType, isYard: false,
  sameAsNext: false, locationId: '', locationName: '', referenceNo: '',
  date: today, time: '12:00', appointment: false, note: '',
  loadingOption: '', contactName: '', contactPhone: '',
  isPartial: false, partialQuantity: '', partialUnit: '', partialWeight: '', partialWeightUnit: 'lbs',
  // Client-side UI concern only — rides along in the stop object and does
  // get sent in the save payload (buildPayload spreads each stop as-is),
  // but the server's INSERT only ever reads its own explicitly named
  // columns (st.stopType, st.locationName, ...), so this extra field is
  // just silently ignored there, never persisted. Tracks whether Weight
  // has been typed into directly, so the Quantity-based auto-calc knows
  // it's safe to keep recomputing Weight vs. a user's own override.
  weightTouched: false,
  openingTimeOverride: '', closingTimeOverride: '',
});

// DB stores rate/value as DECIMAL(10,4), so saved orders come back as
// "2500.0000" / "1.0000". Show them as plain numbers ("2500", "1"); a rate of
// 0 (what an empty Rate saves as) shows as empty, like a brand-new row.
const plainNumber = (v) => (v === null || v === undefined || v === '' ? '' : String(Number(v)));
const plainRate = (v) => (Number(v) ? plainNumber(v) : '');

// $1,200 / -$1,200 (sign before the $, which toLocaleString alone won't do).
const formatMoney = (n) => `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const blankRevenue = (item = 'Freight Charge') => ({
  revenueItem: item, rateMethod: 'Flat', rate: '', value: '1',
});

// Per-trip assignment — Company driver/Owner operator/Outsource, and
// whichever of these the selected type actually needs. Lives on the trip
// object itself (alongside `stops`), not on any one stop.
const blankTripMeta = () => ({
  assignmentType: 'company',
  driverId: '', truckId: '', trailerId: '', ownerOperatorId: '', carrierId: '',
  carrierRate: '', // what we pay the outside carrier (Outsource tab)
});

export default function CreateLoad({ onSaved, onCancel, title = 'New order', loadId = null }) {
  const navigate = useNavigate();
  // ── lookups ──────────────────────────────────────────────────────────────
  const [customers, setCustomers]   = useState([]);
  const [salesmen,  setSalesmen]    = useState([]);
  const [locations, setLocations]   = useState([]);

  // ── Details ──────────────────────────────────────────────────────────────
  const [customerId,           setCustomerId]           = useState('');
  const [customerOrderNumber,  setCustomerOrderNumber]  = useState('');
  const [loadType,             setLoadType]             = useState('FTL');
  const [freightOn,            setFreightOn]            = useState('Order');
  const [salesmanId,           setSalesmanId]           = useState('');
  const [commissionType,       setCommissionType]       = useState('');
  const [commissionValue,      setCommissionValue]      = useState('');
  const [orderNotes,           setOrderNotes]           = useState('');
  const [showAdditionalInfo,   setShowAdditionalInfo]   = useState(false);
  const [scaleTicket,          setScaleTicket]          = useState('');
  const [sealNumber,           setSealNumber]           = useState('');
  const [loadPriority,         setLoadPriority]         = useState('');
  const [customEquipment,      setCustomEquipment]      = useState([]);
  const [customCommodities,    setCustomCommodities]    = useState([]);
  const [customLoadingOptions, setCustomLoadingOptions] = useState([]);
  const [customLoadPriorities, setCustomLoadPriorities] = useState([]);
  const [customCommissionTypes, setCustomCommissionTypes] = useState([]);
  const [customCurrencies,     setCustomCurrencies]     = useState([]);
  const [customRevenueItems,   setCustomRevenueItems]   = useState([]);
  const [customRateMethods,    setCustomRateMethods]    = useState([]);
  const [customTaxBasis,       setCustomTaxBasis]       = useState([]);
  const [shipmentType,         setShipmentType]         = useState('Inter City');
  const [shipmentTypeOpen,     setShipmentTypeOpen]     = useState(false);
  const [currency,             setCurrency]             = useState('CAD');
  // Which Inter Province / Inter State option is even offered depends on
  // where the company itself is registered (Settings > Company Profile) —
  // fetched once below, alongside the other lookups.
  const [companyCountry,       setCompanyCountry]       = useState('Canada');

  // ── Border crossing ──────────────────────────────────────────────────────
  const [crossBorderDirection, setCrossBorderDirection] = useState('Outbound'); // 'Outbound' | 'Inbound'
  const [crossBorderNumber,    setCrossBorderNumber]    = useState(''); // PAPS (Outbound) or PARS (Inbound)
  const [portOfEntry,          setPortOfEntry]          = useState('');
  const [customUsPorts,        setCustomUsPorts]        = useState([]);
  const [customCaPorts,        setCustomCaPorts]        = useState([]);
  const [customsBrokers,       setCustomsBrokers]       = useState([]);
  const [customsBrokerId,      setCustomsBrokerId]      = useState('');
  // Changing direction invalidates whatever port/broker was picked under
  // the old direction's list — clear both rather than leave a stale
  // selection from the wrong country sitting there silently.
  const changeCrossBorderDirection = (dir) => {
    setCrossBorderDirection(dir);
    setPortOfEntry('');
    setCustomsBrokerId('');
  };

  // ── Additional info flags ────────────────────────────────────────────────
  const [flags, setFlags] = useState({
    isCsa: false, isCtpat: false, isPip: false,
    isHazmat: false, isTarp: false, isOverDimension: false,
  });
  const toggleFlag = k => setFlags(f => ({ ...f, [k]: !f[k] }));
  // Freeform detail for Over Dimension (e.g. "load overhangs 3ft on left
  // side") — only shown/relevant once that flag is checked, but kept as its
  // own field rather than folded into it since the flag is a plain boolean.
  const [overDimensionNote, setOverDimensionNote] = useState('');

  // ── Shipments (commodity/value/equipment/etc — no longer paired with stops) ─
  const [shipments, setShipments] = useState([blankShipment()]);

  const updateShipment = (i, field, value) =>
    setShipments(ss => ss.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  // ── Trips (the actual booked movement) ───────────────────────────────────
  // Trip is purely a POSITIONAL grouping — trips[i]'s label ("Trip 1",
  // "Trip 2"...) comes only from its index in this array. A trip is 1+
  // pickups followed by 1+ deliveries: it only closes once a delivery has
  // occurred, so consecutive pickups (or consecutive deliveries) always stay
  // together in the same trip. A new trip is pushed ONLY by "+ Add" (a
  // genuinely separate movement, built via the pickup+delivery popup) or by
  // "+ Add Yard" (a relay point splits one trip into two — yard-as-delivery
  // closes the first, yard-as-pickup opens the second). "+ Add Pickup"/
  // "+ Add Delivery" on a trip's own card always add into THAT SAME trip —
  // never a new one — by inserting at the right position within its stops
  // (a new pickup goes before that trip's first delivery, grouping with the
  // other pickups, instead of always being appended at the end) rather than
  // by ever spinning off trip 02, 03, etc. Starts empty — nothing exists
  // until the first "+ Add".
  const [trips, setTrips] = useState([]);
  const [stopDistances, setStopDistances] = useState([]); // miles for each leg, indexed same as the flattened stop list
  const [yardPicker, setYardPicker] = useState(null); // { tripIndex, stopIndex } or null — which stop's "+ Add Yard" is open
  const stopsListRef = useRef(null);
  // DOM nodes for each trip-group / stop-panel, keyed by index — lets a
  // newly added trip or stop scroll ITSELF into view precisely, instead of
  // blindly scrolling the whole list to its max scrollHeight. That older
  // approach could scroll a tall new trip's OWN header (and its "+ Add
  // Pickup"/"+ Add Delivery" buttons, which sit at the top of the trip
  // card) up past the top edge of the internally-scrolling list — those
  // buttons would then sit just above the visible area, so a real click
  // there landed on the sticky header behind them instead, looking like
  // the click did nothing even though the stop was actually added.
  const tripRefs = useRef(new Map());
  const stopRefs = useRef(new Map());
  const setTripRef = (tripIndex) => (el) => {
    if (el) tripRefs.current.set(tripIndex, el); else tripRefs.current.delete(tripIndex);
  };
  const setStopRef = (tripIndex, stopIndex) => (el) => {
    const key = tripIndex + '-' + stopIndex;
    if (el) stopRefs.current.set(key, el); else stopRefs.current.delete(key);
  };
  // A brand-new trip (from "+ Add"): bring its own header — where "+ Add
  // Pickup"/"+ Add Delivery" live — into view, not just the bottom of
  // whatever it contains.
  const scrollTripIntoView = (tripIndex) => {
    requestAnimationFrame(() => {
      const el = tripRefs.current.get(tripIndex);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  // A pickup/delivery added to an EXISTING trip: bring just that new stop
  // into view with the minimal scroll needed ('nearest'), so the trip's
  // own header/buttons — which the user just clicked and might click
  // again — stay visible too instead of being pushed off the top.
  const scrollStopIntoView = (tripIndex, stopIndex) => {
    requestAnimationFrame(() => {
      const el = stopRefs.current.get(tripIndex + '-' + stopIndex);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  // ── Documents (BOL, POD, etc.) — needs a saved order to attach to ────────
  const [loadDocuments, setLoadDocuments] = useState([]);
  const [docType, setDocType] = useState('Bill of Lading (BOL)');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docsExpanded, setDocsExpanded] = useState(false); // "Full View" button opens a larger modal with the same panel

  const loadDocuments_fetch = useCallback((id) => {
    if (!id) { setLoadDocuments([]); return; }
    api(`/dispatch/loads/${id}/documents`).then(setLoadDocuments).catch(() => setLoadDocuments([]));
  }, []);

  const uploadDocument = async (file) => {
    if (!currentLoadId) { alert('Save the order first — enter a customer and order number, then try again in a moment.'); return; }
    setUploadingDoc(true);
    try {
      const uploaded = await uploadFile('/dispatch/upload', file);
      const created = await api(`/dispatch/loads/${currentLoadId}/documents`, {
        method: 'POST',
        body: JSON.stringify({ documentType: docType, fileUrl: uploaded.url, fileName: uploaded.fileName })
      });
      setLoadDocuments(list => [created, ...list]);
    } catch (e) {
      alert(e.message);
    } finally {
      setUploadingDoc(false);
    }
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Remove "${doc.file_name || doc.document_type}"?`)) return;
    await api(`/dispatch/loads/${currentLoadId}/documents/${doc.id}`, { method: 'DELETE' });
    setLoadDocuments(list => list.filter(d => d.id !== doc.id));
    if (previewDoc?.id === doc.id) setPreviewDoc(null);
  };


  // "+ Add Pickup"/"+ Add Delivery" on a trip's OWN card always adds the
  // stop into that same trip — it never spins off a new one, no matter
  // what that trip already contains. What keeps this consistent with the
  // pickup(s)-then-delivery(s) rule is WHERE the stop lands: a new pickup
  // is inserted right before that trip's first delivery (grouping with the
  // other pickups) instead of always being appended at the very end, so
  // adding a pickup to an already-closed trip can never turn into an
  // invalid pickup-after-delivery inside it. A new delivery always goes at
  // the end, after any existing ones. Only the top-level "+ Add" (a
  // genuinely separate movement) ever starts trip 02, 03, etc.
  const addStopToTrip = (tripIndex, stopType) => {
    const trip = trips[tripIndex];
    const firstDeliveryIdx = trip.stops.findIndex(s => s.stopType === 'delivery');
    const insertAt = stopType === 'pickup' && firstDeliveryIdx !== -1 ? firstDeliveryIdx : trip.stops.length;
    setTrips(ts => {
      const next = ts.map((t, i) => i === tripIndex
        ? { ...t, stops: [...t.stops.slice(0, insertAt), blankStop(stopType), ...t.stops.slice(insertAt)] }
        : t);
      // Still runs through the same recompute reordering uses — this
      // insertion is built to always keep the trip valid on its own, so
      // regrouping here is a no-op/safety net, never a way to re-split it.
      return regroupTrips(next);
    });
    scrollStopIntoView(tripIndex, insertAt);
  };

  const removeStopFromTrip = (tripIndex, stopIndex) =>
    setTrips(ts => ts.map((t, i) => i === tripIndex ? { ...t, stops: t.stops.filter((_, si) => si !== stopIndex) } : t)
      .filter(t => t.stops.length > 0));

  // Recomputes trip boundaries for the ENTIRE stop sequence from scratch:
  // a trip is 1+ pickups followed by 1+ deliveries, closing as soon as a
  // delivery occurs — the next pickup after that starts a new trip. Which
  // trip a stop belongs to is always a function of its position relative to
  // pickup/delivery boundaries in the current order, never anything fixed
  // or cached from when the stop was first added — so any operation that
  // can reorder stops across a pickup/delivery boundary has to run this
  // before the trips array can be trusted again. A freshly-started group
  // inherits its assignment (driver/truck/carrier...) from whichever
  // original trip contributed its first stop — the most sensible owner for
  // a boundary that's only just been created by the reorder.
  const regroupTrips = (ts) => {
    const flat = ts.flatMap(t => t.stops.map(stop => ({ stop, meta: t })));
    const groups = [];
    let current = null;
    let hasDelivery = false;
    flat.forEach(({ stop, meta }) => {
      if (!current || (stop.stopType === 'pickup' && hasDelivery)) {
        current = { meta, stops: [] };
        groups.push(current);
        hasDelivery = false;
      }
      current.stops.push(stop);
      if (stop.stopType === 'delivery') hasDelivery = true;
    });
    return groups.map(g => ({ ...g.meta, stops: g.stops }));
  };

  // Moves a stop up/down. Within its own trip, this is a simple swap. At
  // the edge of a trip, it crosses into the adjacent trip instead of doing
  // nothing — moving a stop up out of the top of one trip and into the
  // bottom of the previous one, or down into the top of the next. Either
  // way, trip_number isn't just carried along with the stop — the whole
  // sequence is regrouped afterward so labels (Trip 1/2/3...) always reflect
  // the new pickup/delivery boundaries, not whatever trip the stop used to
  // sit in.
  const moveStopInTrip = (tripIndex, stopIndex, direction) =>
    setTrips(ts => {
      const next = ts.map(t => ({ ...t, stops: [...t.stops] }));
      const trip = next[tripIndex];
      const target = stopIndex + direction;

      if (target >= 0 && target < trip.stops.length) {
        [trip.stops[stopIndex], trip.stops[target]] = [trip.stops[target], trip.stops[stopIndex]];
        return regroupTrips(next);
      }

      const stop = trip.stops[stopIndex];
      if (direction < 0) {
        if (tripIndex === 0) return ts;
        trip.stops.splice(stopIndex, 1);
        next[tripIndex - 1].stops.push(stop);
      } else {
        if (tripIndex === next.length - 1) return ts;
        trip.stops.splice(stopIndex, 1);
        next[tripIndex + 1].stops.unshift(stop);
      }
      return regroupTrips(next.filter(t => t.stops.length > 0));
    });

  const updateTripStop = (tripIndex, stopIndex, field, value) =>
    setTrips(ts => ts.map((t, i) => i !== tripIndex ? t : {
      ...t, stops: t.stops.map((s, si) => si === stopIndex ? { ...s, [field]: value } : s)
    }));

  // Shared by the main trip stops AND the Add popup's own pickup/delivery
  // fields — the "quick add/edit/delete location inline" flow (from the
  // Location dropdown) needs to know which one it's updating.
  const applyLocationToContext = (context, id, name) => {
    if (!context) return;
    if (context.tripIndex !== undefined) {
      updateTripStop(context.tripIndex, context.stopIndex, 'locationId', id);
      updateTripStop(context.tripIndex, context.stopIndex, 'locationName', name);
    } else if (context.stopPairField) {
      updatePopupEntry(context.stopPairField, context.entryIndex, 'locationId', id);
      updatePopupEntry(context.stopPairField, context.entryIndex, 'locationName', name);
    }
  };

  // "+ Add Yard" on a stop within a trip: a yard is a relay point, so it
  // SPLITS the trip in two — everything up to and including the new yard
  // delivery stays in the current trip, a new trip starts right after it
  // with a yard pickup, followed by whatever stops came after the split
  // point. Auto-numbered (Yard 1, Yard 2...) across the whole order.
  const addYardSplit = (tripIndex, stopIndex, yardLocation) => {
    setTrips(ts => {
      const trip = ts[tripIndex];
      const before = trip.stops.slice(0, stopIndex + 1);
      const after = trip.stops.slice(stopIndex + 1);
      const yardDelivery = { ...blankStop('delivery'), isYard: true, locationId: yardLocation.id, locationName: yardLocation.name };
      const yardPickup = { ...blankStop('pickup'), isYard: true, locationId: yardLocation.id, locationName: yardLocation.name };
      const next = [...ts];
      next[tripIndex] = { ...trip, stops: [...before, yardDelivery] };
      next.splice(tripIndex + 1, 0, { ...blankTripMeta(), stops: [yardPickup, ...after] });
      return next;
    });
  };

  // Order Summary's per-trip Action menu: Copy duplicates a trip in place;
  // Cancel removes it. Both work on the form as it's being built right now.
  // Create Trip / Create Invoice need a saved order to attach to (and a real
  // "edit an existing order" flow, which doesn't exist yet) — not wired up.
  const copyTrip = (tripIndex) => {
    setTrips(ts => {
      const next = [...ts];
      next.splice(tripIndex + 1, 0, { ...ts[tripIndex], stops: ts[tripIndex].stops.map(s => ({ ...s })) });
      return next;
    });
  };
  const cancelTrip = (tripIndex) => {
    if (!window.confirm(`Remove Trip ${tripIndex + 1}? This can't be undone.`)) return;
    setTrips(ts => ts.filter((_, i) => i !== tripIndex));
  };

  // "+ Add" popup — enter a pickup + delivery pair together, becomes a
  // brand new Trip.
  const blankPopupEntry = () => ({
    locationId: '', locationName: '', date: today, time: '12:00', ref: '', note: '',
    contactName: '', contactPhone: '', isPartial: false,
    partialQuantity: '', partialUnit: '', partialWeight: '', partialWeightUnit: 'lbs',
    weightTouched: false, // client-side only, see blankStop's own comment
    openingTimeOverride: '', closingTimeOverride: '',
  });
  const blankStopPair = () => ({
    pickups: [blankPopupEntry()],
    deliveries: [blankPopupEntry()]
  });
  const [stopPairPopup, setStopPairPopup] = useState(false);
  const [stopPairForm, setStopPairForm] = useState(blankStopPair());

  const addPopupEntry = (side) =>
    setStopPairForm(f => ({ ...f, [side]: [...f[side], blankPopupEntry()] }));
  const removePopupEntry = (side, index) =>
    setStopPairForm(f => ({ ...f, [side]: f[side].filter((_, i) => i !== index) }));
  const updatePopupEntry = (side, index, field, value) =>
    setStopPairForm(f => ({ ...f, [side]: f[side].map((e, i) => i === index ? { ...e, [field]: value } : e) }));

  const saveStopPair = () => {
    const pickups = stopPairForm.pickups.map(e => ({
      ...blankStop('pickup'), locationId: e.locationId, locationName: e.locationName,
      date: e.date, time: e.time, referenceNo: e.ref, note: e.note,
      contactName: e.contactName, contactPhone: e.contactPhone,
      isPartial: e.isPartial, partialQuantity: e.partialQuantity,
      partialUnit: e.partialUnit, partialWeight: e.partialWeight, partialWeightUnit: e.partialWeightUnit,
      weightTouched: e.weightTouched,
      openingTimeOverride: e.openingTimeOverride, closingTimeOverride: e.closingTimeOverride,
    }));
    const deliveries = stopPairForm.deliveries.map(e => ({
      ...blankStop('delivery'), locationId: e.locationId, locationName: e.locationName,
      date: e.date, time: e.time, referenceNo: e.ref, note: e.note,
      contactName: e.contactName, contactPhone: e.contactPhone,
      isPartial: e.isPartial, partialQuantity: e.partialQuantity,
      partialUnit: e.partialUnit, partialWeight: e.partialWeight, partialWeightUnit: e.partialWeightUnit,
      weightTouched: e.weightTouched,
      openingTimeOverride: e.openingTimeOverride, closingTimeOverride: e.closingTimeOverride,
    }));
    const newTripIndex = trips.length;
    setTrips(ts => [...ts, { ...blankTripMeta(), stops: [...pickups, ...deliveries] }]);
    setStopPairForm(blankStopPair());
    setStopPairPopup(false);
    scrollTripIntoView(newTripIndex);
  };

  // ── Revenue ──────────────────────────────────────────────────────────────
  const [revenueItems, setRevenueItems] = useState([
    blankRevenue('Freight Charge'),
    blankRevenue('Fuel Surcharge'),
  ]);
  const [taxesBasedOn, setTaxesBasedOn] = useState('Customer');
  const [tax1Rate,     setTax1Rate]     = useState('0.0000');
  const [tax2Rate,     setTax2Rate]     = useState('0.0000');

  // Editing an existing order: fetch it once and populate every piece of
  // state from what's already saved. Trips are reconstructed by grouping
  // stops on trip_number (added specifically so this restore is possible —
  // trip groupings only existed client-side before).
  useEffect(() => {
    if (!loadId) return;
    api(`/dispatch/loads/${loadId}`).then(data => {
      setCustomerId(data.customer_id || '');
      setCustomerOrderNumber(data.customer_order_number || '');
      setLoadType(data.load_type || 'FTL');
      setFreightOn(data.freight_on || 'Order');
      setSalesmanId(data.salesman_id || '');
      setCommissionType(data.commission_type || '');
      setCommissionValue(data.commission_value || '');
      setOrderNotes(data.order_notes || '');
      setCurrency(data.currency || 'CAD');
      setScaleTicket(data.scale_ticket || '');
      setSealNumber(data.seal_number || '');
      setShipmentType(data.shipment_type || 'Inter City');
      setLoadPriority(data.load_priority || '');
      setCrossBorderDirection(data.cross_border_direction || 'Outbound');
      setCrossBorderNumber(data.cross_border_number || '');
      setPortOfEntry(data.port_of_entry || '');
      setCustomsBrokerId(data.customs_broker_id || '');
      setOrderContactId(data.order_contact_id || '');
      setFlags({
        isCsa: !!data.is_csa, isCtpat: !!data.is_ctpat, isPip: !!data.is_pip,
        isHazmat: !!data.is_hazmat, isTarp: !!data.is_tarp, isOverDimension: !!data.is_over_dimension,
      });
      setOverDimensionNote(data.over_dimension_note || '');

      if (data.shipments?.length) {
        setShipments(data.shipments.map(s => ({
          commodity: s.commodity || '', valueOfGoods: s.value_of_goods || '', valueCurrency: s.value_currency || 'CAD',
          equipment: s.equipment || '', numPackages: s.num_packages || '', packageUnit: s.package_unit || '',
          weight: s.weight || '', weightUnit: s.weight_unit || 'lbs',
          temperature: s.temperature || '', tempUnit: s.temp_unit || 'Celsius',
          dimL: s.dim_l || '', dimW: s.dim_w || '', dimH: s.dim_h || '', dimUnit: s.dim_unit || 'in',
        })));
      }

      if (data.stops?.length) {
        const byTrip = {};
        data.stops.forEach(s => {
          const tn = s.trip_number || 1;
          if (!byTrip[tn]) byTrip[tn] = [];
          byTrip[tn].push({
            stopType: s.stop_type, isYard: !!s.is_yard, sameAsNext: !!s.same_as_next,
            locationId: s.location_id || '', locationName: s.location_name || s.location || '',
            referenceNo: s.reference_no || '', date: s.scheduled_at ? String(s.scheduled_at).slice(0, 10) : today,
            time: s.stop_time || '12:00', appointment: !!s.appointment, note: s.note || '',
            loadingOption: s.loading_option || '', contactName: s.contact_name || '', contactPhone: s.contact_phone || '',
            isPartial: !!s.is_partial, partialQuantity: s.partial_quantity || '', partialUnit: s.partial_unit || '',
            partialWeight: s.partial_weight || '', partialWeightUnit: s.partial_weight_unit || 'lbs',
            // Already has a saved weight (or never had partial details at
            // all) — either way, editing this stop shouldn't silently
            // recalculate and overwrite whatever's already there.
            weightTouched: !!s.partial_weight,
            openingTimeOverride: s.opening_time_override ? String(s.opening_time_override).slice(0, 5) : '',
            closingTimeOverride: s.closing_time_override ? String(s.closing_time_override).slice(0, 5) : '',
          });
        });
        const tripNumbers = Object.keys(byTrip).map(Number).sort((a, b) => a - b);
        const assignmentsByTripNumber = {};
        (data.tripAssignments || []).forEach(a => { assignmentsByTripNumber[a.trip_number || 1] = a; });
        setTrips(tripNumbers.map(tn => {
          const a = assignmentsByTripNumber[tn];
          return {
            stops: byTrip[tn],
            assignmentType: a?.assignment_type || 'company',
            driverId: a?.driver_id || '', truckId: a?.truck_id || '', trailerId: a?.trailer_id || '',
            ownerOperatorId: a?.owner_operator_id || '', carrierId: a?.carrier_id || '',
            carrierRate: a?.carrier_rate != null ? String(Number(a.carrier_rate)) : '',
          };
        }));
      }

      if (data.revenue?.length) {
        setRevenueItems(data.revenue.map(r => ({
          revenueItem: r.revenue_item, rateMethod: r.rate_method || 'Flat',
          rate: plainRate(r.rate), value: r.value == null ? '1' : plainNumber(r.value),
        })));
      }

      if (data.taxes?.length) {
        setTaxesBasedOn(data.taxes[0]?.taxes_based_on || 'Customer');
        setTax1Rate(String(data.taxes[0]?.tax_rate ?? '0.0000'));
        setTax2Rate(String(data.taxes[1]?.tax_rate ?? '0.0000'));
      }
    }).catch(e => setError(e.message));
  }, [loadId]);

  const updateRevenue = (i, field, value) =>
    setRevenueItems(rs => rs.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const revAmount = r => {
    const rate = parseFloat(r.rate) || 0;
    const val  = parseFloat(r.value) || 0;
    return rate * val;
  };

  const subTotal   = revenueItems.reduce((s, r) => s + revAmount(r), 0);
  const tax1Amount = subTotal * (parseFloat(tax1Rate) / 100 || 0);
  const tax2Amount = subTotal * (parseFloat(tax2Rate) / 100 || 0);
  const totalTax   = tax1Amount + tax2Amount;
  const totalFreight = subTotal + totalTax;

  // ── Saving ───────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  useEffect(() => {
    api('/dispatch/customers').then(setCustomers).catch(() => {});
    api('/dispatch/salesmen').then(setSalesmen).catch(() => {});
    api('/dispatch/locations').then(setLocations).catch(() => {});
    api('/dispatch/customs-brokers').then(setCustomsBrokers).catch(() => {});
    api('/settings').then(s => setCompanyCountry(s.country || 'Canada')).catch(() => {});
  }, []);

  // ── Add/Edit lookup modal (Customer, Salesman, Location) ─────────────────
  const [activeTab, setActiveTab] = useState('details');

  // Outsource tab — 'outsourcing' feature only (server enforces the same rule).
  const outsourcing = useFeature('outsourcing');
  const [carriers, setCarriers] = useState([]);
  useEffect(() => {
    if (outsourcing) api('/dispatch/carriers').then(setCarriers).catch(() => setCarriers([]));
  }, [outsourcing]);
  const updateTripMeta = (ti, patch) => setTrips(ts => ts.map((t, i) => (i === ti ? { ...t, ...patch } : t)));

  // Driving distance between each consecutive stop — fetched from the
  // backend (Google's Distance Matrix API doesn't support being called
  // straight from the browser). Feeds both Order Summary's per-leg column
  // AND the Shipment tab's auto-computed total Distance field, so this
  // needs to stay current regardless of which tab is open, not just when
  // Order Summary happens to be the active one.
  useEffect(() => {
    const flatStops = trips.flatMap(t => t.stops);
    const addresses = flatStops.map(s => {
      const loc = locations.find(l => l.id == s.locationId);
      return loc ? [loc.address, loc.city, loc.province, loc.postal_code, loc.country].filter(Boolean).join(', ') : null;
    });
    if (addresses.length < 2) { setStopDistances([]); return; }
    api('/dispatch/distances', { method: 'POST', body: { addresses } })
      .then(res => setStopDistances(res.legs || []))
      .catch(() => setStopDistances([]));
  }, [trips, locations]);

  // Sum of every leg — this is what the Shipment tab's Distance field
  // shows. A leg that couldn't be resolved (no address, API miss) is
  // dropped from the sum rather than treated as 0, so one bad leg doesn't
  // silently understate the total.
  const totalDistanceMiles = stopDistances.some(d => d != null)
    ? Math.round(stopDistances.reduce((sum, d) => d != null ? sum + d : sum, 0) * 100) / 100
    : null;

  const [lookupModal, setLookupModal] = useState(null);
  // { kind: 'customer'|'salesman'|'location', mode: 'add'|'edit', id, name, context }
  const [lookupName, setLookupName] = useState('');

  const openAddLookup = (kind, context = null) => {
    setLookupModal({ kind, mode: 'add', id: null, context });
    setLookupName('');
  };

  const LOOKUP_ENDPOINT = { customer: '/dispatch/customers', salesman: '/dispatch/salesmen', location: '/dispatch/locations' };
  const LOOKUP_SETTER   = { customer: setCustomers, salesman: setSalesmen, location: setLocations };

  const saveLookup = async () => {
    if (!lookupName.trim() || !lookupModal) return;
    const { kind, mode, id, context } = lookupModal;
    try {
      const saved = mode === 'add'
        ? await api(LOOKUP_ENDPOINT[kind], { method: 'POST', body: JSON.stringify({ name: lookupName }) })
        : await api(`${LOOKUP_ENDPOINT[kind]}/${id}`, { method: 'PATCH', body: JSON.stringify({ name: lookupName }) });

      LOOKUP_SETTER[kind](list => {
        if (mode === 'add') return [...list, saved];
        return list.map(item => item.id === id ? { ...item, name: lookupName } : item);
      });

      if (mode === 'add') {
        if (kind === 'customer') setCustomerId(saved.id);
        else if (kind === 'salesman') setSalesmanId(saved.id);
        else if (kind === 'location' && context) {
          applyLocationToContext(context, saved.id, saved.name);
        }
      }
      setLookupModal(null);
      setLookupName('');
    } catch (e) { alert(e.message); }
  };

  // ── Manage-options card (Add/Edit/Delete in one place, for every field) ──
  const [manageModal, setManageModal] = useState(null); // { field, context }
  const openManage = (field, context = null) => setManageModal({ field, context });

  const getManageConfig = () => {
    if (!manageModal) return null;
    const { field, context } = manageModal;

    const localConfig = (baseArr, customArr, setCustomArr, currentVal, setVal, label) => ({
      label,
      items: [
        ...baseArr.map(n => ({ key: n, name: n, locked: true })),
        ...customArr.map(n => ({ key: n, name: n, locked: false })),
      ],
      onAdd: (name) => setCustomArr(list => list.includes(name) ? list : [...list, name]),
      onEdit: (key, name) => {
        setCustomArr(list => list.map(o => o === key ? name : o));
        if (currentVal === key) setVal(name);
      },
      onDelete: (key, name) => {
        if (!window.confirm(`Delete "${name}"?`)) return;
        setCustomArr(list => list.filter(o => o !== key));
        if (currentVal === key) setVal('');
      },
    });

    if (field === 'loadPriority') return localConfig(
      ['Low', 'Medium', 'High'],
      customLoadPriorities, setCustomLoadPriorities, loadPriority, setLoadPriority, 'Load Priorities');

    if (field === 'portOfEntry') {
      const isOutbound = context?.direction !== 'Inbound';
      return localConfig(
        isOutbound ? US_PORTS_OF_ENTRY : CA_PORTS_OF_ENTRY,
        isOutbound ? customUsPorts : customCaPorts,
        isOutbound ? setCustomUsPorts : setCustomCaPorts,
        portOfEntry, setPortOfEntry,
        isOutbound ? 'US Ports of Entry' : 'Canadian Ports of Entry');
    }

    if (field === 'commissionType') return localConfig(
      ['%', 'Flat'], customCommissionTypes, setCustomCommissionTypes, commissionType, setCommissionType, 'Commission Types');

    if (field === 'taxBasis') return localConfig(
      ['Customer', 'Carrier'], customTaxBasis, setCustomTaxBasis, taxesBasedOn, setTaxesBasedOn, 'Tax Basis Options');

    if (field === 'equipment') return localConfig(
      ['Dry Van', 'Flatbed', 'Reefer', 'Step Deck', 'Lowboy', 'Tanker'],
      customEquipment, setCustomEquipment, shipments[context.shipIdx]?.equipment,
      (v) => updateShipment(context.shipIdx, 'equipment', v), 'Equipment Types');

    if (field === 'commodity') return localConfig(
      COMMON_COMMODITIES,
      customCommodities, setCustomCommodities, shipments[context.shipIdx]?.commodity,
      (v) => updateShipment(context.shipIdx, 'commodity', v), 'Commodities');

    if (field === 'loadingOption') return localConfig(
      ['Live Load', 'Drop and Hook', 'Ground Load'],
      customLoadingOptions, setCustomLoadingOptions, trips[context.tripIndex]?.stops[context.stopIndex]?.loadingOption,
      (v) => updateTripStop(context.tripIndex, context.stopIndex, 'loadingOption', v), 'Loading Options');

    if (field === 'revenueItem') return localConfig(
      ['Freight Charge', 'Fuel Surcharge', 'Detention', 'Layover', 'TONU', 'Border Crossing', 'Other'],
      customRevenueItems, setCustomRevenueItems, revenueItems[context.ri]?.revenueItem,
      (v) => updateRevenue(context.ri, 'revenueItem', v), 'Revenue Items');

    if (field === 'rateMethod') return localConfig(
      ['Flat', 'Per Mile', 'Per KM', 'Per Hour', 'Per Load'],
      customRateMethods, setCustomRateMethods, revenueItems[context.ri]?.rateMethod,
      (v) => updateRevenue(context.ri, 'rateMethod', v), 'Rate Methods');

    if (field === 'salesman' || field === 'location' || field === 'customer') {
      const list = field === 'salesman' ? salesmen : field === 'location' ? locations : customers;
      const currentVal = field === 'salesman' ? salesmanId
        : field === 'location' ? (context
            ? (context.tripIndex !== undefined
                ? trips[context.tripIndex]?.stops[context.stopIndex]?.locationId
                : stopPairForm[context.stopPairField]?.[context.entryIndex]?.locationId)
            : null)
        : customerId;
      const label = field === 'salesman' ? 'Salesmen' : field === 'location' ? 'Locations' : 'Customers';
      return {
        label,
        items: list.map(o => ({ key: o.id, name: o.name, locked: false })),
        onAdd: async (name) => {
          try {
            const saved = await api(LOOKUP_ENDPOINT[field], { method: 'POST', body: JSON.stringify({ name }) });
            LOOKUP_SETTER[field](l => [...l, saved]);
          } catch (e) { alert(e.message); }
        },
        onEdit: async (id, name) => {
          try {
            await api(`${LOOKUP_ENDPOINT[field]}/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
            LOOKUP_SETTER[field](l => l.map(item => item.id === id ? { ...item, name } : item));
            if (currentVal == id) {
              if (field === 'salesman') setSalesmanId(id);
              else if (field === 'customer') setCustomerId(id);
              else if (field === 'location' && context) {
                applyLocationToContext(context, id, name);
              }
            }
          } catch (e) { alert(e.message); }
        },
        onDelete: async (id, name) => {
          if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
          try {
            await api(`${LOOKUP_ENDPOINT[field]}/${id}`, { method: 'DELETE' });
            LOOKUP_SETTER[field](l => l.filter(item => item.id != id));
            if (currentVal == id) {
              if (field === 'salesman') setSalesmanId('');
              else if (field === 'customer') setCustomerId('');
              else if (field === 'location' && context) {
                applyLocationToContext(context, '', '');
              }
            }
          } catch (e) { alert(e.message); }
        },
      };
    }
    return null;
  };

  // ── Customer search (now via CustomerSidePanel — same pattern as Location) ──
  const selectedCustomerName = customers.find(c => c.id == customerId)?.name || '';

  // "+ Add New Customer" now opens the full Customer form (Control's own
  // CustomerFormModal) pre-filled with whatever was typed, instead of
  // creating a bare-name record — on save it lands back on this same
  // order with the new customer selected.
  const [addCustomerPrefill, setAddCustomerPrefill] = useState(null); // null = closed, '' or a string = open
  const [customerDetail, setCustomerDetail] = useState(null);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [orderContactId, setOrderContactId] = useState('');

  // Credit figures (limit/used/available) are computed server-side on every
  // request, so this is re-run whenever a customer is picked (even the same
  // one again) and after each successful save of this order. The ref drops a
  // slow response for a previously-picked customer that arrives after a newer
  // pick, so the box can't show another customer's numbers.
  const latestCustomerRequest = useRef(null);
  // Brand-new order that hasn't been explicitly saved yet = a draft.
  const isNewOrder = !loadId;
  const explicitlySavedRef = useRef(false);
  const loadCustomerDetail = useCallback((id, { contacts = true } = {}) => {
    latestCustomerRequest.current = id;
    if (!id) { setCustomerDetail(null); setCustomerContacts([]); return; }
    api(`/dispatch/customers/${id}`)
      .then(d => { if (latestCustomerRequest.current === id) setCustomerDetail(d); })
      .catch(() => { if (latestCustomerRequest.current === id) setCustomerDetail(null); });
    if (contacts) {
      api(`/dispatch/customers/${id}/contacts`)
        .then(c => { if (latestCustomerRequest.current === id) setCustomerContacts(c); })
        .catch(() => { if (latestCustomerRequest.current === id) setCustomerContacts([]); });
    }
  }, []);

  useEffect(() => { loadCustomerDetail(customerId); }, [customerId, loadCustomerDetail]);

  const onCustomerSaved = (saved) => {
    setCustomers(list => {
      const exists = list.some(c => c.id === saved.id);
      return exists ? list.map(c => c.id === saved.id ? saved : c) : [...list, saved];
    });
    setCustomerId(saved.id);
    setAddCustomerPrefill(null);
  };

  // "+ Add New Customs Broker" — same pattern as Customer/Location: opens
  // the full Customs Broker form (Control's own CustomsBrokerFormModal),
  // pre-filled with whatever was typed and the broker_country matching
  // whichever direction is currently selected, so the new broker shows up
  // in this same picker immediately without a second edit.
  const [addCustomsBrokerPrefill, setAddCustomsBrokerPrefill] = useState(null); // null = closed

  const onCustomsBrokerSaved = (saved) => {
    setCustomsBrokers(list => {
      const exists = list.some(b => b.id === saved.id);
      return exists ? list.map(b => b.id === saved.id ? saved : b) : [...list, saved];
    });
    setCustomsBrokerId(saved.id);
    setAddCustomsBrokerPrefill(null);
  };

  // Order Contact — a contact on the selected customer, picked specifically
  // for this order. "+ Add New" saves a brand new contact onto the
  // customer's own contact list (not just this order) and selects it here.
  const [addContactPopup, setAddContactPopup] = useState(false);
  const [newContactForm, setNewContactForm] = useState({ name: '', phone: '', email: '' });
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);

  const saveNewOrderContact = async () => {
    if (!newContactForm.name.trim() || !customerId) return;
    try {
      const saved = await api(`/dispatch/customers/${customerId}/contacts`, {
        method: 'POST',
        body: JSON.stringify({ name: newContactForm.name, phone: newContactForm.phone, email: newContactForm.email })
      });
      setCustomerContacts(list => [...list, saved]);
      setOrderContactId(saved.id);
      setNewContactForm({ name: '', phone: '', email: '' });
      setAddContactPopup(false);
    } catch (e) {
      alert(e.message);
    }
  };

  // "+ Add New Location" — same pattern as Customer: opens the full
  // Location form (Control's own LocationFormModal), pre-filled with
  // whatever was typed. On save, the new location is applied back to
  // whichever field opened it — the same context shape (tripIndex+
  // stopIndex, or stopPairField+entryIndex) applyLocationToContext
  // already knows how to route.
  const [addLocationRequest, setAddLocationRequest] = useState(null); // { name, context } or null

  const onLocationSaved = (saved) => {
    setLocations(list => {
      const exists = list.some(l => l.id === saved.id);
      return exists ? list.map(l => l.id === saved.id ? saved : l) : [...list, saved];
    });
    if (addLocationRequest) applyLocationToContext(addLocationRequest.context, saved.id, saved.name);
    setAddLocationRequest(null);
  };


  // Auto-save: currentLoadId tracks the record once it exists (starts as
  // the loadId prop when editing, or null for a brand-new order — the
  // first auto-save creates it and this gets set from the response).
  const [currentLoadId, setCurrentLoadId] = useState(loadId);
  useEffect(() => { loadDocuments_fetch(currentLoadId); }, [currentLoadId, loadDocuments_fetch]);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [saveError,  setSaveError]  = useState('');

  const buildPayload = () => ({
    customerId: customerId || null,
    customerOrderNumber, loadType, freightOn,
    salesmanId: salesmanId || null,
    commissionType, commissionValue,
    orderNotes, currency,
    scaleTicket, sealNumber, shipmentType, loadPriority,
    crossBorderDirection, crossBorderNumber, portOfEntry,
    customsBrokerId: customsBrokerId || null,
    orderContactId: orderContactId || null,
    ...flags,
    overDimensionNote,
    shipments,
    stops: trips.flatMap((t, ti) => t.stops.map(s => ({ ...s, tripNumber: ti + 1 }))),
    trips: trips.map((t, ti) => ({
      tripNumber: ti + 1,
      assignmentType: t.assignmentType || 'company',
      driverId: t.driverId || null, truckId: t.truckId || null, trailerId: t.trailerId || null,
      ownerOperatorId: t.ownerOperatorId || null, carrierId: t.carrierId || null,
      carrierRate: t.assignmentType === 'outsource' && t.carrierRate !== '' ? Number(t.carrierRate) : null,
    })),
    revenueItems: revenueItems.map(r => ({ ...r, amount: revAmount(r) })),
    taxes: [
      { rate: parseFloat(tax1Rate) || 0, amount: tax1Amount, basedOn: taxesBasedOn },
      { rate: parseFloat(tax2Rate) || 0, amount: tax2Amount, basedOn: taxesBasedOn },
    ],
  });

  // Debounced auto-save — fires ~1.2s after the last change, and only once
  // there's something worth saving (a customer and an order number, the
  // two required fields). Creates the order on the first save if it
  // doesn't exist yet, otherwise updates it in place.
  useEffect(() => {
    if (!customerId || !customerOrderNumber?.trim()) return;
    const timer = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        // A new order stays a draft (not counted toward credit, not listed)
        // until Save Load / Create Trip — see server/src/db/drafts.js.
        const payload = { ...buildPayload(), ...(isNewOrder && !explicitlySavedRef.current ? { isDraft: true } : {}) };
        const result = currentLoadId
          ? await api(`/dispatch/loads/${currentLoadId}`, { method: 'PATCH', body: JSON.stringify(payload) })
          : await api('/dispatch/loads', { method: 'POST', body: JSON.stringify(payload) });
        if (!currentLoadId) setCurrentLoadId(result.id);
        setSaveStatus('saved');
        setSaveError('');
        // This order now counts toward the customer's balance — refresh USED/AVAILABLE.
        loadCustomerDetail(customerId, { contacts: false });
      } catch (e) {
        // Surface the real reason (e.g. a schema mismatch from a migration
        // that hasn't been run yet) instead of a generic message — this is
        // the only feedback the user gets when autosave fails, since it's
        // silent otherwise.
        console.error('Order autosave failed:', e.message);
        setSaveError(e.message || 'Unknown error');
        setSaveStatus('error');
      }
    }, 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerId, customerOrderNumber, loadType, freightOn, salesmanId,
    commissionType, commissionValue, orderNotes, currency, scaleTicket, sealNumber,
    shipmentType, loadPriority, flags, overDimensionNote, shipments, trips, revenueItems,
    tax1Rate, tax2Rate, taxesBasedOn, orderContactId,
    crossBorderDirection, crossBorderNumber, portOfEntry, customsBrokerId,
  ]);

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true); setError('');
    // Set before the request so an autosave firing meanwhile can't re-mark it a draft.
    explicitlySavedRef.current = true;
    try {
      const payload = { ...buildPayload(), isDraft: false };
      const result = currentLoadId
        ? await api(`/dispatch/loads/${currentLoadId}`, { method: 'PATCH', body: JSON.stringify(payload) })
        : await api('/dispatch/loads', { method: 'POST', body: JSON.stringify(payload) });
      onSaved && onSaved(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // "Create Trip" (Shipment tab only) — same save as "Save Load", plus one
  // extra call flipping the order's dispatch status from Unassigned to
  // Assigned. Kept as a separate status endpoint (rather than folding
  // status into buildPayload/the main save) so "Save Load" elsewhere never
  // has any way to accidentally change dispatch status — it only ever
  // touches order details, exactly like today.
  const handleCreateTrip = async () => {
    setSaving(true); setError('');
    explicitlySavedRef.current = true;
    try {
      const payload = { ...buildPayload(), isDraft: false };
      const result = currentLoadId
        ? await api(`/dispatch/loads/${currentLoadId}`, { method: 'PATCH', body: JSON.stringify(payload) })
        : await api('/dispatch/loads', { method: 'POST', body: JSON.stringify(payload) });
      const id = currentLoadId || result.id;
      navigate(`/orders/${id}/create-trip`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  // Weight-per-unit for the auto-calc below: the order's own declared
  // Weight ÷ No of Packages (Shipment card) — not a generic "average
  // weight per skid" table, since that would vary wildly by commodity and
  // there's nowhere in this app that already tracks such a thing. Using
  // the order's own numbers means the estimate is only ever as good as
  // what's already been entered, and simply doesn't fire (leaving Weight
  // untouched) if either field is blank.
  const firstShipment = shipments[0];
  const weightPerUnit = (() => {
    const w = parseFloat(firstShipment?.weight);
    const n = parseFloat(firstShipment?.numPackages);
    return (w > 0 && n > 0) ? w / n : null;
  })();

  // Partial Load Details — Quantity / Units / Weight, used on the full
  // Stop panel and on each entry in the Add popup. Weight auto-fills from
  // Quantity × weightPerUnit until the user types into Weight directly
  // (weightTouched), at which point it stops recalculating — still fully
  // editable either way, this only controls whether Quantity keeps it in
  // sync automatically.
  // Called as a plain function ({PartialLoadFields(...)}), never as <PartialLoadFields />: it is defined
  // inside CreateLoad, so as a JSX component React would see a brand-new
  // component type on every keystroke and remount it, dropping input focus.
  const PartialLoadFields = ({ quantity, unit, weight, weightUnit, weightTouched, onQuantity, onUnit, onWeight, onWeightUnit, onWeightTouched }) => {
    const handleQuantity = (val) => {
      onQuantity(val);
      if (!weightTouched && weightPerUnit != null) {
        const q = parseFloat(val);
        if (!isNaN(q)) onWeight(String(Math.round(q * weightPerUnit * 100) / 100));
      }
    };
    const handleWeight = (val) => {
      onWeight(val);
      onWeightTouched(true);
    };
    return (
    <div className="form-row">
      <div className="field flex1">
        <label>Quantity</label>
        <input type="number" placeholder="e.g. 6" value={quantity} onChange={e => handleQuantity(e.target.value)} />
      </div>
      <div className="field flex1">
        <label>Units</label>
        <select value={unit} onChange={e => onUnit(e.target.value)}>
          <option value="">—</option>
          <option>Skids</option><option>Pallets</option><option>Pieces</option>
          <option>Boxes</option><option>Drums</option><option>Rolls</option><option>Cases</option>
        </select>
      </div>
      <div className="field flex1">
        <label>Weight{weightPerUnit != null && !weightTouched && quantity ? ' (auto)' : ''}</label>
        <input type="number" placeholder="e.g. 1200" value={weight} onChange={e => handleWeight(e.target.value)} />
      </div>
      <div className="field flex1">
        <label>Weight Unit</label>
        <select value={weightUnit} onChange={e => onWeightUnit(e.target.value)}>
          <option value="lbs">lbs</option>
          <option value="kg">kg</option>
        </select>
      </div>
    </div>
    );
  };

  // Called as a plain function ({DocumentsPanel(...)}), never as <DocumentsPanel />: it is defined
  // inside CreateLoad, so as a JSX component React would see a brand-new
  // component type on every keystroke and remount it, dropping input focus.
  const DocumentsPanel = () => (
    !currentLoadId ? (
      <div className="empty">Enter a customer and order number first — documents can be attached once the order is saved.</div>
    ) : (
      <div className="documents-layout">
        <div className="documents-left">
          <label>Document Type<br/>
            <select value={docType} onChange={e => setDocType(e.target.value)}>
              <option>Bill of Lading (BOL)</option>
              <option>Proof of Delivery (POD)</option>
              <option>Rate Confirmation</option>
              <option>Invoice</option>
              <option>Other</option>
            </select>
          </label>
          <div className="documents-save-as">
            Saves as: <strong>{customerOrderNumber || 'this order'} {docType}</strong>
          </div>
          <label className="documents-dropzone">
            <input type="file" style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) uploadDocument(e.target.files[0]); e.target.value = ''; }} />
            <div>{uploadingDoc ? 'Uploading…' : 'Drag & drop or click to upload'}</div>
            <div className="documents-dropzone-hint">Max 15MB · PDF, XLS, CSV, JPG, PNG</div>
          </label>
          <div className="documents-file-list">
            {loadDocuments.length === 0 && <div className="documents-empty">No files yet.</div>}
            {loadDocuments.map(doc => (
              <div key={doc.id} className={'documents-file-row' + (previewDoc?.id === doc.id ? ' active' : '')}
                onClick={() => setPreviewDoc(doc)}>
                <div>
                  <div className="documents-file-name">{doc.file_name || doc.document_type}</div>
                  <div className="documents-file-type">{doc.document_type}</div>
                </div>
                <button type="button" className="btn-remove" onClick={e => { e.stopPropagation(); deleteDocument(doc); }}>✕</button>
              </div>
            ))}
          </div>
        </div>
        <div className="documents-preview">
          {!previewDoc && <span className="documents-empty">Select a file to preview</span>}
          {previewDoc && /\.(jpe?g|png|gif|webp)$/i.test(previewDoc.file_url) && (
            <img src={`${API_ORIGIN}${previewDoc.file_url}`} alt={previewDoc.file_name} style={{ maxWidth: '100%', maxHeight: '100%' }} />
          )}
          {previewDoc && /\.pdf$/i.test(previewDoc.file_url) && (
            <iframe title="preview" src={`${API_ORIGIN}${previewDoc.file_url}`} style={{ width: '100%', height: '100%', border: 'none' }} />
          )}
          {previewDoc && !/\.(jpe?g|png|gif|webp|pdf)$/i.test(previewDoc.file_url) && (
            <a href={`${API_ORIGIN}${previewDoc.file_url}`} target="_blank" rel="noreferrer">Open {previewDoc.file_name}</a>
          )}
        </div>
      </div>
    )
  );

  // Called as a plain function ({StopPanel(...)}), never as <StopPanel />: it is defined
  // inside CreateLoad, so as a JSX component React would see a brand-new
  // component type on every keystroke and remount it, dropping input focus.
  const StopPanel = ({ tripIndex, stopIndex }) => {
    const trip = trips[tripIndex];
    const stop = trip.stops[stopIndex];
    const stopType = stop.stopType;
    const label = stopType === 'pickup' ? 'PICKUP' : 'DELIVERY';
    const dateLabel = stopType === 'pickup' ? 'Pickup Date' : 'Delivery Date';
    const isFirstOverall = tripIndex === 0 && stopIndex === 0;
    const isLastOverall = tripIndex === trips.length - 1 && stopIndex === trip.stops.length - 1;
    return (
      <div className={'stop-panel ' + (stopType === 'pickup' ? 'stop-panel-pickup' : 'stop-panel-delivery')}>
        <div className="stop-header">
          <span className="stop-label">
            {stop.isYard ? `${label} — ${stop.locationName || 'YARD'}` : label}
          </span>
          <StopHoursField locationId={stop.locationId} locations={locations}
            overrideStart={stop.openingTimeOverride} overrideEnd={stop.closingTimeOverride}
            onChangeStart={v => updateTripStop(tripIndex, stopIndex, 'openingTimeOverride', v)}
            onChangeEnd={v => updateTripStop(tripIndex, stopIndex, 'closingTimeOverride', v)} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative', marginLeft: 'auto' }}>
            <button type="button" className="btn-secondary"
              onClick={() => setYardPicker(yardPicker?.tripIndex === tripIndex && yardPicker?.stopIndex === stopIndex ? null : { tripIndex, stopIndex })}>
              + Add Yard
            </button>
            {yardPicker?.tripIndex === tripIndex && yardPicker?.stopIndex === stopIndex && (
              <>
                <div className="trip-action-backdrop" onClick={() => setYardPicker(null)} />
                <div className="trip-action-menu">
                  {locations.filter(l => l.is_yard).length === 0 && (
                    <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--cl-text-dim, #8a94a6)', maxWidth: 220 }}>
                      No yards registered yet — check "This is a Yard" on a location in Control &gt; Locations.
                    </div>
                  )}
                  {locations.filter(l => l.is_yard).map(l => (
                    <button type="button" key={l.id} onClick={() => { addYardSplit(tripIndex, stopIndex, l); setYardPicker(null); }}>
                      {l.name}
                    </button>
                  ))}
                </div>
              </>
            )}
            <button type="button" onClick={() => moveStopInTrip(tripIndex, stopIndex, -1)} disabled={isFirstOverall} title="Move up">↑</button>
            <button type="button" onClick={() => moveStopInTrip(tripIndex, stopIndex, 1)} disabled={isLastOverall} title="Move down">↓</button>
            <button type="button" className="btn-remove" onClick={() => removeStopFromTrip(tripIndex, stopIndex)} title="Remove stop">✕</button>
          </div>
        </div>
        <div className="form-row">
          <div className="field flex2">
            <label>Location <span className="req">*</span></label>
            <LocationSidePanel valueId={stop.locationId} valueName={stop.locationName}
              onChange={(id, name) => {
                updateTripStop(tripIndex, stopIndex, 'locationId', id);
                updateTripStop(tripIndex, stopIndex, 'locationName', name);
              }}
              locations={locations} kind="location" context={{ tripIndex, stopIndex }}
              onOpenAddForm={(name, ctx) => setAddLocationRequest({ name, context: ctx })} />
            <LocationDetailText locationId={stop.locationId} locations={locations} />
          </div>
          <div className="field flex1">
            <label>Reference No.</label>
            <input placeholder="Ref No" value={stop.referenceNo}
              onChange={e => updateTripStop(tripIndex, stopIndex, 'referenceNo', e.target.value)} />
          </div>
          <div className="field flex1">
            <label>{dateLabel} <span className="req">*</span></label>
            <input type="date" value={stop.date}
              onChange={e => updateTripStop(tripIndex, stopIndex, 'date', e.target.value)} />
          </div>
          <div className="field flex1">
            <label>Time <span className="req">*</span></label>
            <input type="time" value={stop.time}
              onChange={e => updateTripStop(tripIndex, stopIndex, 'time', e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <div className="field flex1">
            <label>Loading Option</label>
            <EditableSelect value={stop.loadingOption} onChange={val => updateTripStop(tripIndex, stopIndex, 'loadingOption', val)}
              baseOptions={['Live Load', 'Drop and Hook', 'Ground Load']}
              customOptions={customLoadingOptions} setCustomOptions={setCustomLoadingOptions}
              promptLabel="loading option" onManage={() => openManage('loadingOption', { tripIndex, stopIndex })} />
          </div>
          <div className="field flex1">
            <label>Load Contact</label>
            <input placeholder="Contact name" value={stop.contactName}
              onChange={e => updateTripStop(tripIndex, stopIndex, 'contactName', e.target.value)} />
          </div>
          <div className="field flex1">
            <label>Contact Phone</label>
            <PhoneInput value={stop.contactPhone}
              onChange={v => updateTripStop(tripIndex, stopIndex, 'contactPhone', v)} />
          </div>
        </div>
        <div className="form-row">
          <div className="field">
            <label>Appointment</label>
            <div className="radio-group">
              <label><input type="radio" name={`appt-${tripIndex}-${stopIndex}`}
                checked={stop.appointment === true}
                onChange={() => updateTripStop(tripIndex, stopIndex, 'appointment', true)} /> Yes</label>
              <label><input type="radio" name={`appt-${tripIndex}-${stopIndex}`}
                checked={stop.appointment === false}
                onChange={() => updateTripStop(tripIndex, stopIndex, 'appointment', false)} /> No</label>
            </div>
          </div>
          <div className="field">
            <label>&nbsp;</label>
            <button type="button" className="btn-secondary"
              onClick={() => updateTripStop(tripIndex, stopIndex, 'isPartial', !stop.isPartial)}>
              {stop.isPartial ? '✓ Partial Load Details' : 'Partial Load Details'}
            </button>
          </div>
        </div>
        {stop.isPartial && (
          PartialLoadFields({
            quantity: stop.partialQuantity,
            unit: stop.partialUnit,
            weight: stop.partialWeight,
            weightUnit: stop.partialWeightUnit,
            weightTouched: stop.weightTouched,
            onQuantity: v => updateTripStop(tripIndex, stopIndex, 'partialQuantity', v),
            onUnit: v => updateTripStop(tripIndex, stopIndex, 'partialUnit', v),
            onWeight: v => updateTripStop(tripIndex, stopIndex, 'partialWeight', v),
            onWeightUnit: v => updateTripStop(tripIndex, stopIndex, 'partialWeightUnit', v),
            onWeightTouched: v => updateTripStop(tripIndex, stopIndex, 'weightTouched', v),
          })
        )}
        <div className="form-row">
          <div className="field full">
            <label>Note</label>
            <textarea rows={2} value={stop.note}
              onChange={e => updateTripStop(tripIndex, stopIndex, 'note', e.target.value)} />
          </div>
        </div>
      </div>
    );
  };

  // If Cancel is hit on a brand-new order, auto-save may have already
  // created it in the background — clean that up rather than leaving an
  // orphaned draft behind. Only applies when this session created it
  // (loadId prop was null to start); an order opened for editing is never
  // touched here.
  const handleCancel = async () => {
    if (!loadId && currentLoadId) {
      try { await api(`/dispatch/loads/${currentLoadId}`, { method: 'DELETE' }); } catch (e) { /* best-effort cleanup */ }
    }
    onCancel && onCancel();
  };

  return (
    <div className="create-load">
      <div className="form-title-bar">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="13" y2="16"/></svg>
        <span>{title}</span>
        {saveStatus === 'saving' && <span className="autosave-indicator">Saving…</span>}
        {saveStatus === 'saved' && <span className="autosave-indicator saved">✓ Saved</span>}
        {saveStatus === 'error' && <span className="autosave-indicator error" title={saveError}>⚠ Couldn't save{saveError ? `: ${saveError}` : ''}</span>}
        {/* Nothing on this form saves — not just these checkboxes, EVERY
            field — until both of these exist, since that's what creates
            the order autosave writes to. Silent otherwise, which is
            exactly what made a checkbox "not saving" look like a bug. */}
        {saveStatus === 'idle' && (!customerId || !customerOrderNumber?.trim()) && (
          <span className="autosave-indicator hint">Enter a Customer and Customer Order # to start saving this order</span>
        )}
      </div>
      {/* Add/Edit lookup modal (Customer, Salesman, Location) */}
      {lookupModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{lookupModal.mode === 'add' ? 'Add' : 'Edit'} {lookupModal.kind}</h3>
            <input autoFocus placeholder={`${lookupModal.kind} name`} value={lookupName}
              onChange={e => setLookupName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveLookup()} />
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setLookupModal(null)}>Cancel</button>
              <button type="button" className="btn-primary" onClick={saveLookup}>{lookupModal.mode === 'add' ? 'Add' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Manage-options card (Add/Edit/Delete in one place) */}
      {manageModal && (() => {
        const cfg = getManageConfig();
        return cfg && <ManageOptionsCard {...cfg} onClose={() => setManageModal(null)} />;
      })()}

      {/* Additional Info modal */}
      {showAdditionalInfo && (
        <div className="modal-overlay" onClick={() => setShowAdditionalInfo(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Additional Info</h3>
              <button type="button" className="modal-close"
                onClick={() => setShowAdditionalInfo(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="field flex1">
                  <label>Shipment For</label>
                  <select value={freightOn} onChange={e => setFreightOn(e.target.value)}>
                    <option value="Order">Order</option>
                    <option value="Shipment">Shipment</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Customer — full Customer form, pre-filled with whatever was
          typed in the side panel. Saving lands back on this same order with
          the new customer selected (via onCustomerSaved). */}
      {addCustomerPrefill !== null && (
        <CustomerFormModal
          customer={null}
          initialName={addCustomerPrefill}
          allCustomers={customers}
          onClose={() => setAddCustomerPrefill(null)}
          onSaved={onCustomerSaved}
        />
      )}

      {/* Edit the selected customer (e.g. to manage their contacts) —
          opened from the Order Contact field's "Manage" option. */}
      {editCustomerOpen && customerDetail && (
        <CustomerFormModal
          customer={customerDetail}
          allCustomers={customers}
          onClose={() => setEditCustomerOpen(false)}
          onSaved={(saved) => { onCustomerSaved(saved); setEditCustomerOpen(false); loadCustomerDetail(customerId); }}
        />
      )}

      {/* Add New Order Contact — a quick name/phone/email form, saved onto
          the customer's own contact list, then selected for this order. */}
      {addContactPopup && (
        <div className="modal-overlay" onClick={() => setAddContactPopup(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Add New Contact</h3>
            <label>Name <span className="req">*</span><br/>
              <input value={newContactForm.name} onChange={e => setNewContactForm(f => ({ ...f, name: e.target.value }))} />
            </label>
            <label>Phone<br/>
              <PhoneInput value={newContactForm.phone} onChange={v => setNewContactForm(f => ({ ...f, phone: v }))} />
            </label>
            <label>Email<br/>
              <input value={newContactForm.email} onChange={e => setNewContactForm(f => ({ ...f, email: e.target.value }))} />
            </label>
            <div className="modal-actions">
              <button type="button" onClick={() => setAddContactPopup(false)}>Cancel</button>
              <button type="button" className="btn-primary" onClick={saveNewOrderContact}>Add Contact</button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Location — same pattern: full Location form, pre-filled,
          lands back on the same order with the field it was opened from
          filled in (via onLocationSaved + applyLocationToContext). */}
      {addLocationRequest && (
        <LocationFormModal
          location={{ name: addLocationRequest.name }}
          allLocations={locations}
          onClose={() => setAddLocationRequest(null)}
          onSaved={onLocationSaved}
        />
      )}

      {/* Add New Customs Broker — same pattern: full Customs Broker form,
          pre-filled with whatever was typed plus the broker_country matching
          the currently-selected border-crossing direction, lands back on
          this same order with the new broker selected. */}
      {addCustomsBrokerPrefill !== null && (
        <CustomsBrokerFormModal
          customsBroker={{ name: addCustomsBrokerPrefill, broker_country: crossBorderDirection === 'Inbound' ? 'Canada' : 'US' }}
          onClose={() => setAddCustomsBrokerPrefill(null)}
          onSaved={onCustomsBrokerSaved}
        />
      )}

      {/* Add Stop popup — build a full trip: one or more pickups, one or more deliveries */}
      {stopPairPopup && (
        <div className="modal-overlay stop-pair-overlay" onClick={() => setStopPairPopup(false)}>
          <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Pickup &amp; Delivery</h3>
              <button type="button" className="modal-close" onClick={() => setStopPairPopup(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="stop-pair-columns">
                <div className="stop-pair-col stop-pair-col-pickup">
                  <div className="stop-header stop-header-with-hours">
                    <span className="stop-label">PICKUP</span>
                    <StopHoursField locationId={stopPairForm.pickups[0]?.locationId} locations={locations}
                      overrideStart={stopPairForm.pickups[0]?.openingTimeOverride} overrideEnd={stopPairForm.pickups[0]?.closingTimeOverride}
                      onChangeStart={v => updatePopupEntry('pickups', 0, 'openingTimeOverride', v)}
                      onChangeEnd={v => updatePopupEntry('pickups', 0, 'closingTimeOverride', v)} />
                  </div>
                  {stopPairForm.pickups.map((entry, ei) => (
                    <div key={ei} className="popup-entry">
                      {ei > 0 && (
                        <div className="popup-entry-header">
                          <span>Pickup #{ei + 1}</span>
                          <StopHoursField locationId={entry.locationId} locations={locations}
                            overrideStart={entry.openingTimeOverride} overrideEnd={entry.closingTimeOverride}
                            onChangeStart={v => updatePopupEntry('pickups', ei, 'openingTimeOverride', v)}
                            onChangeEnd={v => updatePopupEntry('pickups', ei, 'closingTimeOverride', v)} />
                          <button type="button" className="btn-remove" onClick={() => removePopupEntry('pickups', ei)}>✕</button>
                        </div>
                      )}
                      <label>Location <span className="req">*</span></label>
                      <LocationSidePanel valueId={entry.locationId} valueName={entry.locationName}
                        onChange={(id, name) => { updatePopupEntry('pickups', ei, 'locationId', id); updatePopupEntry('pickups', ei, 'locationName', name); }}
                        locations={locations} kind="location" context={{ stopPairField: 'pickups', entryIndex: ei }}
                        onOpenAddForm={(name, ctx) => setAddLocationRequest({ name, context: ctx })} />
                      <LocationDetailText locationId={entry.locationId} locations={locations} />
                      <label>Reference No.</label>
                      <input value={entry.ref} onChange={e => updatePopupEntry('pickups', ei, 'ref', e.target.value)} />
                      <div className="form-row">
                        <div className="field flex1">
                          <label>Pickup Date <span className="req">*</span></label>
                          <input type="date" value={entry.date} onChange={e => updatePopupEntry('pickups', ei, 'date', e.target.value)} />
                        </div>
                        <div className="field flex1">
                          <label>Time <span className="req">*</span></label>
                          <input type="time" value={entry.time} onChange={e => updatePopupEntry('pickups', ei, 'time', e.target.value)} />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="field flex1">
                          <label>Load Contact</label>
                          <input placeholder="Contact name" value={entry.contactName} onChange={e => updatePopupEntry('pickups', ei, 'contactName', e.target.value)} />
                        </div>
                        <div className="field flex1">
                          <label>Contact Phone</label>
                          <PhoneInput value={entry.contactPhone} onChange={v => updatePopupEntry('pickups', ei, 'contactPhone', v)} />
                        </div>
                      </div>
                      <button type="button" className="btn-secondary" style={{ marginTop: 4 }}
                        onClick={() => updatePopupEntry('pickups', ei, 'isPartial', !entry.isPartial)}>
                        {entry.isPartial ? '✓ Partial Load Details' : 'Partial Load Details'}
                      </button>
                      {entry.isPartial && (
                        PartialLoadFields({
                          quantity: entry.partialQuantity,
                          unit: entry.partialUnit,
                          weight: entry.partialWeight,
                          weightUnit: entry.partialWeightUnit,
                          weightTouched: entry.weightTouched,
                          onQuantity: v => updatePopupEntry('pickups', ei, 'partialQuantity', v),
                          onUnit: v => updatePopupEntry('pickups', ei, 'partialUnit', v),
                          onWeight: v => updatePopupEntry('pickups', ei, 'partialWeight', v),
                          onWeightUnit: v => updatePopupEntry('pickups', ei, 'partialWeightUnit', v),
                          onWeightTouched: v => updatePopupEntry('pickups', ei, 'weightTouched', v),
                        })
                      )}
                      <label>Note</label>
                      <textarea rows={2} value={entry.note} onChange={e => updatePopupEntry('pickups', ei, 'note', e.target.value)} />
                    </div>
                  ))}
                  <button type="button" className="btn-secondary" onClick={() => addPopupEntry('pickups')}>+ Add Another Pickup</button>
                </div>

                <div className="stop-pair-col stop-pair-col-delivery">
                  <div className="stop-header stop-header-with-hours">
                    <span className="stop-label">DELIVERY</span>
                    <StopHoursField locationId={stopPairForm.deliveries[0]?.locationId} locations={locations}
                      overrideStart={stopPairForm.deliveries[0]?.openingTimeOverride} overrideEnd={stopPairForm.deliveries[0]?.closingTimeOverride}
                      onChangeStart={v => updatePopupEntry('deliveries', 0, 'openingTimeOverride', v)}
                      onChangeEnd={v => updatePopupEntry('deliveries', 0, 'closingTimeOverride', v)} />
                  </div>
                  {stopPairForm.deliveries.map((entry, ei) => (
                    <div key={ei} className="popup-entry">
                      {ei > 0 && (
                        <div className="popup-entry-header">
                          <span>Delivery #{ei + 1}</span>
                          <StopHoursField locationId={entry.locationId} locations={locations}
                            overrideStart={entry.openingTimeOverride} overrideEnd={entry.closingTimeOverride}
                            onChangeStart={v => updatePopupEntry('deliveries', ei, 'openingTimeOverride', v)}
                            onChangeEnd={v => updatePopupEntry('deliveries', ei, 'closingTimeOverride', v)} />
                          <button type="button" className="btn-remove" onClick={() => removePopupEntry('deliveries', ei)}>✕</button>
                        </div>
                      )}
                      <label>Location <span className="req">*</span></label>
                      <LocationSidePanel valueId={entry.locationId} valueName={entry.locationName}
                        onChange={(id, name) => { updatePopupEntry('deliveries', ei, 'locationId', id); updatePopupEntry('deliveries', ei, 'locationName', name); }}
                        locations={locations} kind="location" context={{ stopPairField: 'deliveries', entryIndex: ei }}
                        onOpenAddForm={(name, ctx) => setAddLocationRequest({ name, context: ctx })} />
                      <LocationDetailText locationId={entry.locationId} locations={locations} />
                      <label>Reference No.</label>
                      <input value={entry.ref} onChange={e => updatePopupEntry('deliveries', ei, 'ref', e.target.value)} />
                      <div className="form-row">
                        <div className="field flex1">
                          <label>Delivery Date <span className="req">*</span></label>
                          <input type="date" value={entry.date} onChange={e => updatePopupEntry('deliveries', ei, 'date', e.target.value)} />
                        </div>
                        <div className="field flex1">
                          <label>Time <span className="req">*</span></label>
                          <input type="time" value={entry.time} onChange={e => updatePopupEntry('deliveries', ei, 'time', e.target.value)} />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="field flex1">
                          <label>Load Contact</label>
                          <input placeholder="Contact name" value={entry.contactName} onChange={e => updatePopupEntry('deliveries', ei, 'contactName', e.target.value)} />
                        </div>
                        <div className="field flex1">
                          <label>Contact Phone</label>
                          <PhoneInput value={entry.contactPhone} onChange={v => updatePopupEntry('deliveries', ei, 'contactPhone', v)} />
                        </div>
                      </div>
                      <button type="button" className="btn-secondary" style={{ marginTop: 4 }}
                        onClick={() => updatePopupEntry('deliveries', ei, 'isPartial', !entry.isPartial)}>
                        {entry.isPartial ? '✓ Partial Load Details' : 'Partial Load Details'}
                      </button>
                      {entry.isPartial && (
                        PartialLoadFields({
                          quantity: entry.partialQuantity,
                          unit: entry.partialUnit,
                          weight: entry.partialWeight,
                          weightUnit: entry.partialWeightUnit,
                          weightTouched: entry.weightTouched,
                          onQuantity: v => updatePopupEntry('deliveries', ei, 'partialQuantity', v),
                          onUnit: v => updatePopupEntry('deliveries', ei, 'partialUnit', v),
                          onWeight: v => updatePopupEntry('deliveries', ei, 'partialWeight', v),
                          onWeightUnit: v => updatePopupEntry('deliveries', ei, 'partialWeightUnit', v),
                          onWeightTouched: v => updatePopupEntry('deliveries', ei, 'weightTouched', v),
                        })
                      )}
                      <label>Note</label>
                      <textarea rows={2} value={entry.note} onChange={e => updatePopupEntry('deliveries', ei, 'note', e.target.value)} />
                    </div>
                  ))}
                  <button type="button" className="btn-secondary" onClick={() => addPopupEntry('deliveries')}>+ Add Another Delivery</button>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setStopPairPopup(false)}>Cancel</button>
              <button type="button" className="btn-primary" onClick={saveStopPair}>Add to Stops</button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-tabs">
          <button type="button" className={activeTab === 'details' ? 'form-tab active' : 'form-tab'}
            onClick={() => setActiveTab('details')}>Details</button>
          <button type="button" className={activeTab === 'shipment' ? 'form-tab active' : 'form-tab'}
            onClick={() => setActiveTab('shipment')}>Shipment</button>
          <button type="button" className={activeTab === 'summary' ? 'form-tab active' : 'form-tab'}
            onClick={() => setActiveTab('summary')}>Order Summary</button>
          {outsourcing && (
            <button type="button" className={activeTab === 'outsource' ? 'form-tab active' : 'form-tab'}
              onClick={() => setActiveTab('outsource')}>Outsource</button>
          )}
        </div>

        <div style={{ display: activeTab === 'details' ? 'block' : 'none' }}>
        {/* ── DETAILS ──────────────────────────────────────────────── */}
        <div className="card card-standalone customer-sticky-header">
          <div className="section-bar"><span>Customer</span></div>
          <div className="form-row details-grid-6">
            <div className="field customer-combo-field flex2">
              <label>Customer <span className="req">*</span></label>
              <CustomerSidePanel
                valueId={customerId} valueName={selectedCustomerName}
                onChange={(id) => {
                  // Re-picking the same customer doesn't change customerId, so
                  // the effect won't refetch — refresh the credit box directly.
                  if (String(id) === String(customerId)) loadCustomerDetail(id);
                  setCustomerId(id);
                }}
                customers={customers}
                onOpenAddForm={(name) => setAddCustomerPrefill(name)}
              />
              {customerDetail && (
                <div className="customer-detail-panel">
                  {(() => {
                    const limit = Number(customerDetail.credit_limit) || 0;
                    const used = Number(customerDetail.creditUsed) || 0;
                    const available = Number(customerDetail.creditAvailable) || 0;
                    // No limit set = credit checks are off for this customer
                    // (see the Customer form) — nothing to be "over".
                    const hasLimit = limit > 0;
                    const over = hasLimit && available < 0;
                    return (
                      <div className="customer-credit-row">
                        <div className="customer-credit-item">
                          <span className="customer-credit-label">Credit Limit</span>
                          <span className="customer-credit-value">{hasLimit ? `${formatMoney(limit)} ${customerDetail.currency || 'CDN'}` : 'No limit'}</span>
                        </div>
                        <div className="customer-credit-item">
                          <span className="customer-credit-label">Used</span>
                          <span className="customer-credit-value">{formatMoney(used)}</span>
                        </div>
                        <div className="customer-credit-item">
                          <span className="customer-credit-label">Available</span>
                          <span className={'customer-credit-value' + (over ? ' over' : '')}>
                            {hasLimit ? formatMoney(available) : '—'}
                          </span>
                          {over && <span className="credit-over-flag">Over credit limit</span>}
                        </div>
                        {/* Browser-only preview of this unsaved order's effect —
                            USED above is saved orders only; this order isn't
                            counted until Save Load. */}
                        {hasLimit && isNewOrder && totalFreight > 0 && (() => {
                          const after = available - totalFreight;
                          return (
                            <div className="customer-credit-item customer-credit-preview" title="Preview only — not saved or counted until you click Save Load">
                              <span className="customer-credit-label">After this order</span>
                              <span className={'customer-credit-value' + (after < 0 ? ' over' : '')}>{formatMoney(after)}</span>
                              <span className="customer-credit-hint">preview · not saved</span>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                  <div className="customer-detail-grid">
                    {customerDetail.contact_name && <div><span className="customer-detail-label">Contact</span> {customerDetail.contact_name}</div>}
                    {customerDetail.phone && <div><span className="customer-detail-label">Phone</span> {displayPhone(customerDetail.phone)}</div>}
                    {customerDetail.contact_email && <div><span className="customer-detail-label">Email</span> {customerDetail.contact_email}</div>}
                    {customerDetail.billing_terms && <div><span className="customer-detail-label">Billing Terms</span> {customerDetail.billing_terms}</div>}
                    {(customerDetail.address || customerDetail.city) && (
                      <div className="customer-detail-full">
                        <span className="customer-detail-label">Address</span>
                        {[customerDetail.address, customerDetail.city, customerDetail.province, customerDetail.postal_code].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="customer-order-contact-row">
                    <label>Order Contact</label>
                    <LookupSelect value={orderContactId} onChange={setOrderContactId}
                      options={customerContacts.map(c => ({ id: c.id, name: `${c.name}${c.phone ? ' — ' + displayPhone(c.phone) : ''}` }))}
                      kind="orderContact"
                      openAdd={() => setAddContactPopup(true)}
                      onManage={() => setEditCustomerOpen(true)}
                      placeholder="Select contact for this order" />
                  </div>
                </div>
              )}
            </div>
            <div className="field">
              <label>Shipment Type</label>
              <div className="combo">
                <button type="button" className="combo-toggle"
                  onClick={() => setShipmentTypeOpen(o => !o)}>
                  <span>{shipmentType}</span>
                  <span className={'combo-caret' + (shipmentTypeOpen ? ' open' : '')}>▾</span>
                </button>
                {shipmentTypeOpen && (
                  <>
                    <div className="combo-backdrop" onClick={() => setShipmentTypeOpen(false)} />
                    <div className="combo-panel">
                      <div className="combo-list">
                        {[
                          'Inter City',
                          ...(companyCountry !== 'United States' ? ['Inter Province'] : []),
                          ...(companyCountry === 'United States' ? ['Inter State'] : []),
                          'Cross Border',
                        ].map(o => (
                          <div key={o}
                            className={'combo-option' + (o === shipmentType ? ' selected' : '')}
                            onClick={() => { setShipmentType(o); setShipmentTypeOpen(false); }}>
                            {o}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="field">
              <label>Customer Order # <span className="req">*</span></label>
              <input value={customerOrderNumber}
                onChange={e => setCustomerOrderNumber(e.target.value)} />
            </div>
            <div className="field">
              <label>Load Type</label>
              <SlideToggle value={loadType} onChange={setLoadType}
                leftValue="FTL" rightValue="LTL" leftLabel="FTL" rightLabel="LTL" />
            </div>
            <div className="field">
              <label>Freight on</label>
              <SlideToggle value={freightOn} onChange={setFreightOn}
                leftValue="Order" rightValue="Shipment" leftLabel="Order" rightLabel="Shipment" />
            </div>
            <div className="field">
              <label>Salesman</label>
              <LookupSelect value={salesmanId} onChange={setSalesmanId}
                options={salesmen} kind="salesman" openAdd={openAddLookup} onManage={openManage} />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label>Commission</label>
              <div className="commission-row">
                <EditableSelect value={commissionType} onChange={setCommissionType}
                  baseOptions={['%', 'Flat']}
                  customOptions={customCommissionTypes} setCustomOptions={setCustomCommissionTypes}
                  promptLabel="commission type" onManage={() => openManage('commissionType')} />
                <input type="number" placeholder="0" value={commissionValue}
                  onChange={e => setCommissionValue(e.target.value)} />
              </div>
            </div>
            <div className="field">
              <label>Load Priority</label>
              <EditableSelect value={loadPriority} onChange={setLoadPriority}
                baseOptions={['Low', 'Medium', 'High']}
                customOptions={customLoadPriorities} setCustomOptions={setCustomLoadPriorities}
                promptLabel="load priority" onManage={() => openManage('loadPriority')} />
            </div>
            <div className="field flex2">
              <label>Order Notes</label>
              <textarea rows={2} value={orderNotes}
                onChange={e => setOrderNotes(e.target.value)} />
            </div>
            <div className="field">
              <button type="button" className="link-toggle"
                onClick={() => setShowAdditionalInfo(true)}>
                Additional info+
              </button>
            </div>
          </div>
        </div>
        </div>

        <div style={{ display: activeTab === 'details' && shipmentType === 'Cross Border' ? 'block' : 'none' }}>
        {/* ── BORDER CROSSING (only for Cross Border shipments) ─────────── */}
        <div className="card card-standalone">
          <div className="section-bar"><span>Border Crossing</span></div>
          <div className="form-row">
            <div className="field">
              <label>Direction</label>
              <SlideToggle value={crossBorderDirection} onChange={changeCrossBorderDirection}
                leftValue="Outbound" rightValue="Inbound" leftLabel="Outbound" rightLabel="Inbound" />
            </div>
            <div className="field">
              <label>{crossBorderDirection === 'Inbound' ? 'PARS' : 'PAPS'} #</label>
              <input value={crossBorderNumber} onChange={e => setCrossBorderNumber(e.target.value)}
                placeholder={crossBorderDirection === 'Inbound' ? 'PARS number' : 'PAPS number'} />
            </div>
            <div className="field flex1">
              <label>Port of Entry</label>
              <EditableSelect value={portOfEntry} onChange={setPortOfEntry}
                baseOptions={crossBorderDirection === 'Inbound' ? CA_PORTS_OF_ENTRY : US_PORTS_OF_ENTRY}
                customOptions={crossBorderDirection === 'Inbound' ? customCaPorts : customUsPorts}
                setCustomOptions={crossBorderDirection === 'Inbound' ? setCustomCaPorts : setCustomUsPorts}
                promptLabel="port of entry"
                onManage={() => openManage('portOfEntry', { direction: crossBorderDirection })} />
            </div>
            <div className="field flex2">
              <label>Customs Broker</label>
              <CustomsBrokerSidePanel
                valueId={customsBrokerId}
                valueName={customsBrokers.find(b => b.id == customsBrokerId)?.name || ''}
                onChange={(id) => setCustomsBrokerId(id)}
                brokers={customsBrokers.filter(b => (b.broker_country || '') === (crossBorderDirection === 'Inbound' ? 'Canada' : 'US'))}
                onOpenAddForm={(name) => setAddCustomsBrokerPrefill(name)}
              />
            </div>
          </div>
        </div>
        </div>

        <div className="shipment-tab-wrapper" style={{ display: activeTab === 'shipment' ? 'flex' : 'none' }}>
        {/* ── SHIPMENTS (sticky at top of this tab while Stops scrolls below) ── */}
        <div className="shipment-sticky-header">
        {shipments.length > 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button type="button" className="btn-secondary"
              onClick={() => {
                if (window.confirm('Delete all shipment blocks? This clears every commodity entry on this order.')) {
                  setShipments([blankShipment()]);
                }
              }}>
              Delete All Shipments
            </button>
          </div>
        )}
        {shipments.map((s, si) => (
          <div className="card card-standalone" key={si}>
            <div className="section-bar">
              <span>Shipment</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {si === shipments.length - 1 && (
                  <button type="button" className="btn-plus"
                    onClick={() => setShipments(ss => [...ss, blankShipment()])}>+</button>
                )}
                {shipments.length > 1 && (
                  <button type="button" className="btn-remove"
                    onClick={() => setShipments(ss => ss.filter((_, i) => i !== si))}>✕</button>
                )}
              </div>
            </div>

            {/* Commodity row */}
            <div className="form-row">
              <div className="field flex2">
                <label>Commodity <span className="req">*</span></label>
                <EditableSelect value={s.commodity} onChange={val => updateShipment(si, 'commodity', val)}
                  baseOptions={COMMON_COMMODITIES}
                  customOptions={customCommodities} setCustomOptions={setCustomCommodities}
                  promptLabel="commodity" onManage={() => openManage('commodity', { shipIdx: si })} />
              </div>
              <div className="field flex1">
                <label>Value of goods</label>
                <div className="input-addon">
                  <input type="number" value={s.valueOfGoods}
                    onChange={e => updateShipment(si, 'valueOfGoods', e.target.value)} />
                  <select value={s.valueCurrency}
                    onChange={e => updateShipment(si, 'valueCurrency', e.target.value)}>
                    <option>CAD</option><option>USD</option>
                  </select>
                </div>
              </div>
              <div className="field flex1">
                <label>Equipment</label>
                <EditableSelect value={s.equipment} onChange={val => updateShipment(si, 'equipment', val)}
                  baseOptions={['Dry Van', 'Flatbed', 'Reefer', 'Step Deck', 'Lowboy', 'Tanker']}
                  customOptions={customEquipment} setCustomOptions={setCustomEquipment}
                  promptLabel="equipment type" onManage={() => openManage('equipment', { shipIdx: si })} />
              </div>
              <div className="field flex1">
                <label>No of Packages</label>
                <div className="input-addon">
                  <input type="number" value={s.numPackages}
                    onChange={e => updateShipment(si, 'numPackages', e.target.value)} />
                  <select value={s.packageUnit}
                    onChange={e => updateShipment(si, 'packageUnit', e.target.value)}>
                    <option value="">Select</option>
                    <option>Skids</option><option>Boxes</option>
                    <option>Drums</option><option>Pallets</option>
                  </select>
                </div>
              </div>
              <div className="field flex1">
                <label>Weight</label>
                <div className="input-addon">
                  <input type="number" value={s.weight}
                    onChange={e => updateShipment(si, 'weight', e.target.value)} />
                  <select value={s.weightUnit}
                    onChange={e => updateShipment(si, 'weightUnit', e.target.value)}>
                    <option>lbs</option><option>kg</option>
                  </select>
                </div>
              </div>
              <div className="field flex1">
                <label>Temperature</label>
                <div className="input-addon">
                  <input type="number" value={s.temperature}
                    onChange={e => updateShipment(si, 'temperature', e.target.value)} />
                  <select value={s.tempUnit}
                    onChange={e => updateShipment(si, 'tempUnit', e.target.value)}>
                    <option>Celsius</option><option>Fahrenheit</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Dimensions */}
            <div className="form-row">
              <div className="field">
                <label>Dimension (L×W×H)</label>
                <div className="dim-row">
                  <input type="number" placeholder="L" value={s.dimL}
                    onChange={e => updateShipment(si, 'dimL', e.target.value)} />
                  <input type="number" placeholder="W" value={s.dimW}
                    onChange={e => updateShipment(si, 'dimW', e.target.value)} />
                  <input type="number" placeholder="H" value={s.dimH}
                    onChange={e => updateShipment(si, 'dimH', e.target.value)} />
                  <select value={s.dimUnit}
                    onChange={e => updateShipment(si, 'dimUnit', e.target.value)}>
                    <option>in</option><option>cm</option><option>ft</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Distance + Additional Info */}
            <div className="form-row distance-row">
              <div className="field flex1">
                <label>Distance (auto)</label>
                <input readOnly value={totalDistanceMiles != null ? `${totalDistanceMiles} mi` : '—'}
                  title="Calculated automatically from the stop sequence below — add, remove, or reorder stops to change it." />
              </div>
              <div className="field flex3">
                <label>Additional Info</label>
                <div className="checkbox-row">
                  {[
                    ['isCsa','CSA'],['isCtpat','CTPAT'],['isPip','PIP'],
                    ['isHazmat','HAZMAT'],['isTarp','TARP'],['isOverDimension','Over Dimension'],
                  ].map(([k, label]) => (
                    <label key={k} className="check-label">
                      <input type="checkbox" checked={flags[k]} onChange={() => toggleFlag(k)} />
                      {label}
                    </label>
                  ))}
                </div>
                {flags.isOverDimension && (
                  <input style={{ marginTop: 6 }} placeholder='Over Dimension note — e.g. "load overhangs 3ft on left side"'
                    value={overDimensionNote} onChange={e => setOverDimensionNote(e.target.value)} />
                )}
              </div>
              <div className="field flex1">
                <label>Scale Ticket#</label>
                <input value={scaleTicket} onChange={e => setScaleTicket(e.target.value)} />
              </div>
              <div className="field flex1">
                <label>Seal #</label>
                <input value={sealNumber} onChange={e => setSealNumber(e.target.value)} />
              </div>
            </div>
          </div>
        ))}
        </div>

        {/* ── TRIPS (each is a numbered group of stops — Trip 1, Trip 2...) ── */}
        <div className="card card-standalone stops-scroll-area">
          <div className="section-bar" style={{ flexShrink: 0 }}>
            <span>Stops</span>
            {/* The only way to start a genuinely new trip — every
                "+ Add Pickup"/"+ Add Delivery" button below always adds
                into that SAME trip, never a new one, no matter what that
                trip already contains. */}
            <button type="button" className="section-bar-btn" onClick={() => setStopPairPopup(true)}>+ Add</button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-dim, #8a94a6)', margin: '0 0 14px', flexShrink: 0 }}>
            "+ Add" opens a popup for a pickup + delivery pair — that becomes a new Trip. "+ Add Pickup"/
            "+ Add Delivery" on a trip below add more stops into that SAME trip. "+ Add Yard" on any stop
            splits the trip there — a relay point for two drivers.
          </div>
          <div className="stops-list-scroll" ref={stopsListRef}>
          {trips.length === 0 && <div className="empty">No stops yet — click "+ Add" above.</div>}
          {trips.map((trip, ti) => (
            <div key={ti} className="trip-group" ref={setTripRef(ti)}>
              <div className="trip-group-header">
                <span>{customerOrderNumber ? `${customerOrderNumber}-${String(ti + 1).padStart(2, '0')}` : `Trip ${ti + 1}`}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn-secondary" onClick={() => addStopToTrip(ti, 'pickup')}>+ Add Pickup</button>
                  <button type="button" className="btn-secondary" onClick={() => addStopToTrip(ti, 'delivery')}>+ Add Delivery</button>
                </div>
              </div>

              {trip.stops.map((stop, si) => (
                <div key={si} ref={setStopRef(ti, si)}>
                  {StopPanel({ tripIndex: ti, stopIndex: si })}
                </div>
              ))}
            </div>
          ))}
          </div>
        </div>
        </div>

        {/* ── ORDER SUMMARY ─────────────────────────────────────────────── */}
        <div className="summary-tab-wrapper" style={{ display: activeTab === 'summary' ? 'flex' : 'none' }}>
          <div className="summary-layout">
            <div className="summary-main">
              <div className="card card-standalone summary-order-details-sticky">
                <div className="section-bar"><span>Order Details</span></div>
                <div className="summary-detail-grid">
                  <div><span className="summary-detail-label">Customer</span> {selectedCustomerName || '—'}</div>
                  <div><span className="summary-detail-label">Order #</span> {customerOrderNumber || '—'}</div>
                  <div><span className="summary-detail-label">Load Type</span> {loadType}</div>
                  <div><span className="summary-detail-label">Freight On</span> {freightOn}</div>
                  <div><span className="summary-detail-label">Salesman</span> {salesmen.find(s => s.id == salesmanId)?.name || '—'}</div>
                  <div><span className="summary-detail-label">Commission</span> {commissionValue ? `${commissionValue}${commissionType || ''}` : '—'}</div>
                </div>
                {orderNotes && <div className="summary-detail-full"><span className="summary-detail-label">Notes</span> {orderNotes}</div>}
              </div>

              <div className="card card-standalone summary-flex-card">
                <div className="section-bar"><span>Stops</span></div>
                {trips.length === 0 ? <div className="empty">No stops added yet.</div> : (
                  <div className="summary-fixed-scroll">
                  <table className="summary-table">
                    <thead><tr><th>Stops</th><th>Location</th><th>City/Province</th><th>Date</th><th>No Of Commodity</th><th>Distance</th></tr></thead>
                    <tbody>
                      {trips.flatMap(t => t.stops).map((s, i) => {
                        const loc = locations.find(l => l.id == s.locationId);
                        const cityProvince = loc ? [loc.city, loc.province].filter(Boolean).join(', ') : '—';
                        return (
                          <tr key={i}>
                            <td><span className={'stop-type-badge ' + (s.stopType === 'pickup' ? 'pickup' : 'delivery')}>{s.stopType === 'pickup' ? 'P' : 'D'}</span></td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{s.locationName || '—'}{s.isYard ? ' (Yard)' : ''}</div>
                              {loc?.address && <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{loc.address}</div>}
                            </td>
                            <td>{cityProvince}</td>
                            <td>{s.date || '—'} {s.time || ''}</td>
                            <td>{s.isPartial && s.partialQuantity ? `${s.partialQuantity}${s.partialUnit ? ' ' + s.partialUnit : ''}` : shipments.length}</td>
                            <td>{i === 0 ? '—' : (stopDistances[i - 1] != null ? `${stopDistances[i - 1]} mi` : '—')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>

              <div className="card card-standalone summary-flex-card">
                <div className="section-bar"><span>Shipment</span></div>
                {trips.length === 0 ? <div className="empty">No trips yet — add stops in the Shipment tab.</div> : (
                  <div className="summary-fixed-scroll">
                  <table className="summary-table">
                    <thead>
                      <tr>
                        <th>Action</th><th>Status</th><th>Commodity</th>
                        <th>Pickup Date &amp; Location</th><th>Pickup Note</th>
                        <th>Drop Date &amp; Location</th><th>Drop Note</th>
                        <th>Pickup Appt.</th><th>Delivery Appt.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trips.map((t, ti) => {
                        const pickups = t.stops.filter(s => s.stopType === 'pickup');
                        const deliveries = t.stops.filter(s => s.stopType === 'delivery');
                        const firstPickup = pickups[0];
                        const lastDelivery = deliveries[deliveries.length - 1];
                        return (
                          <tr key={ti}>
                            <td>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button type="button" className="icon-action-btn" title="Edit in Shipment tab" onClick={() => setActiveTab('shipment')}>✎</button>
                                <button type="button" className="icon-action-btn danger" title="Cancel this trip" onClick={() => cancelTrip(ti)}>🗑</button>
                              </div>
                            </td>
                            <td><span className="status-badge planned">PLANNED</span></td>
                            <td>{shipments[0]?.commodity || '—'}</td>
                            <td>
                              {firstPickup ? <>{firstPickup.date} {firstPickup.time}<br />{firstPickup.locationName}{pickups.length > 1 ? ` +${pickups.length - 1} more` : ''}</> : '—'}
                            </td>
                            <td>{firstPickup?.note || '—'}</td>
                            <td>
                              {lastDelivery ? <>{lastDelivery.date} {lastDelivery.time}<br />{lastDelivery.locationName}{deliveries.length > 1 ? ` +${deliveries.length - 1} more` : ''}</> : '—'}
                            </td>
                            <td>{lastDelivery?.note || '—'}</td>
                            <td>{firstPickup?.appointment ? 'Yes' : 'No'}</td>
                            <td>{lastDelivery?.appointment ? 'Yes' : 'No'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </div>

            <div className="summary-side">
              <div className="card card-standalone summary-revenue-card">
                <div className="section-bar"><span>Revenue</span></div>
                <div className="summary-revenue-list">
                  {revenueItems.map((r, i) => (
                    <div key={i} className="summary-revenue-row">
                      <span>{r.revenueItem || '—'}</span>
                      <span>${revAmount(r).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="summary-total-row"><span>Subtotal</span><span>${subTotal.toFixed(2)}</span></div>
                <div className="summary-total-row"><span>Total Tax</span><span>${totalTax.toFixed(2)}</span></div>
                <div className="summary-total-row gross"><span>Gross Total ({currency})</span><span>${totalFreight.toFixed(2)}</span></div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: activeTab === 'details' ? 'block' : 'none' }}>
        {/* ── REVENUE ───────────────────────────────────────────────── */}
        <div className="card card-standalone">
          <div className="section-bar"><span>Revenue</span></div>
          <div className="section-title-row">
            <select value={currency} onChange={e => setCurrency(e.target.value)} className="currency-select">
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="USD">USD - US Dollar</option>
            </select>
          </div>

          <table className="revenue-table">
            <thead>
              <tr>
                <th>Revenue Item <span className="req">*</span></th>
                <th>Rate Method <span className="req">*</span></th>
                <th>Rate</th>
                <th>Value</th>
                <th>Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {revenueItems.map((r, ri) => (
                <tr key={ri}>
                  <td>
                    <EditableSelect value={r.revenueItem} onChange={val => updateRevenue(ri, 'revenueItem', val)}
                      baseOptions={['Freight Charge', 'Fuel Surcharge', 'Detention', 'Layover', 'TONU', 'Border Crossing', 'Other']}
                      customOptions={customRevenueItems} setCustomOptions={setCustomRevenueItems}
                      promptLabel="revenue item" onManage={() => openManage('revenueItem', { ri })} />
                  </td>
                  <td>
                    <EditableSelect value={r.rateMethod} onChange={val => updateRevenue(ri, 'rateMethod', val)}
                      baseOptions={['Flat', 'Per Mile', 'Per KM', 'Per Hour', 'Per Load']}
                      customOptions={customRateMethods} setCustomOptions={setCustomRateMethods}
                      promptLabel="rate method" onManage={() => openManage('rateMethod', { ri })} />
                  </td>
                  <td>
                    <input type="number" value={r.rate}
                      onChange={e => updateRevenue(ri, 'rate', e.target.value)} />
                  </td>
                  <td>
                    <input type="number" value={r.value}
                      onChange={e => updateRevenue(ri, 'value', e.target.value)} />
                  </td>
                  <td className="amount-cell">{revAmount(r).toFixed(2)}</td>
                  <td>
                    {revenueItems.length > 1 && (
                      <button type="button" className="btn-remove-sm"
                        onClick={() => setRevenueItems(rs => rs.filter((_, i) => i !== ri))}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="revenue-footer">
            <button type="button" className="btn-add-charge"
              onClick={() => setRevenueItems(rs => [...rs, blankRevenue('Other')])}>
              + Other Charge
            </button>
            <div className="taxes-panel">
              <div className="taxes-row">
                <label>Taxes Based On</label>
                <EditableSelect value={taxesBasedOn} onChange={setTaxesBasedOn}
                  baseOptions={['Customer', 'Carrier']}
                  customOptions={customTaxBasis} setCustomOptions={setCustomTaxBasis}
                  promptLabel="tax basis" onManage={() => openManage('taxBasis')} />
              </div>
              <div className="tax-line">
                <label>Tax 1</label>
                <input type="number" step="0.0001" value={tax1Rate}
                  onChange={e => setTax1Rate(e.target.value)} />
                <span>%</span>
                <span className="tax-amount">{tax1Amount.toFixed(2)}</span>
              </div>
              <div className="tax-line">
                <label>Tax 2</label>
                <input type="number" step="0.0001" value={tax2Rate}
                  onChange={e => setTax2Rate(e.target.value)} />
                <span>%</span>
                <span className="tax-amount">{tax2Amount.toFixed(2)}</span>
              </div>
              <div className="tax-line total-tax-line">
                <label>TOTAL TAX</label>
                <span className="tax-amount">{totalTax.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="total-freight-bar">
            <span>TOTAL FREIGHT ({currency})</span>
            <span>=</span>
            <strong>{totalFreight.toFixed(2)}</strong>
          </div>
        </div>

        {/* ── DOCUMENTS (embedded here, with a button to expand to a full-page view) ── */}
        <div className="card card-standalone">
          <div className="section-bar">
            <span>Documents</span>
            <button type="button" className="documents-expand-btn" onClick={() => setDocsExpanded(true)} title="Open full page view">
              ⤢ Full View
            </button>
          </div>
          {DocumentsPanel()}
        </div>
        </div>

        {docsExpanded && (
          <div className="modal-overlay documents-fullpage-overlay" onClick={() => setDocsExpanded(false)}>
            <div className="modal documents-fullpage-modal" onClick={e => e.stopPropagation()}>
              <div className="section-bar">
                <span>Documents</span>
                <button type="button" className="modal-close" onClick={() => setDocsExpanded(false)}>✕</button>
              </div>
              <div style={{ padding: 20 }}>
                {DocumentsPanel()}
              </div>
            </div>
          </div>
        )}

        {/* ── OUTSOURCE ('outsourcing' feature) ────────────────────────────
            Per trip: outsource it to an outside carrier (Control > Carrier)
            at a carrier rate. Saved on the trip's assignment
            (trip_assignments.assignment_type = 'outsource', carrier_id,
            carrier_rate), so the Trips list / Create Trip page show the
            carrier exactly as for any outsourced trip. */}
        {outsourcing && activeTab === 'outsource' && (() => {
          const outsourced = trips.filter(t => t.assignmentType === 'outsource');
          const carrierCost = outsourced.reduce((sum, t) => sum + (Number(t.carrierRate) || 0), 0);
          const margin = subTotal - carrierCost;
          const marginPct = subTotal > 0 ? (margin / subTotal) * 100 : null;
          const money = (n) => `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          return (
            <div className="card card-standalone outsource-panel">
              <div className="section-bar"><span>Outsource</span></div>
              {trips.length === 0 ? (
                <div className="outsource-empty">
                  Add pickup and delivery stops on the Shipment tab first — each trip can then be outsourced here.
                </div>
              ) : (
                <>
                  {trips.map((t, ti) => {
                    const first = t.stops.find(st => st.stopType === 'pickup') || t.stops[0];
                    const last = [...t.stops].reverse().find(st => st.stopType === 'delivery') || t.stops[t.stops.length - 1];
                    const on = t.assignmentType === 'outsource';
                    return (
                      <div className={'outsource-trip' + (on ? ' on' : '')} key={ti}>
                        <div className="outsource-trip-head">
                          <strong>Trip {ti + 1}</strong>
                          <span className="outsource-trip-route">{first?.locationName || '—'} → {last?.locationName || '—'}</span>
                          <label className="outsource-switch">
                            <input type="checkbox" checked={on}
                              onChange={e => updateTripMeta(ti, e.target.checked
                                ? { assignmentType: 'outsource', driverId: '', truckId: '', trailerId: '', ownerOperatorId: '' }
                                : { assignmentType: 'company', carrierId: '', carrierRate: '' })} />
                            Outsource this trip
                          </label>
                        </div>
                        {on && (
                          <div className="form-row">
                            <div className="field flex2">
                              <label>Carrier</label>
                              <select value={t.carrierId || ''} onChange={e => updateTripMeta(ti, { carrierId: e.target.value })}>
                                <option value="">Select carrier</option>
                                {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </select>
                              {carriers.length === 0 && <span className="outsource-hint">No carriers yet — add them in Control › Carrier.</span>}
                            </div>
                            <div className="field flex1">
                              <label>Carrier rate ({currency})</label>
                              <input type="number" min="0" step="0.01" placeholder="0.00" value={t.carrierRate}
                                onChange={e => updateTripMeta(ti, { carrierRate: e.target.value })} />
                            </div>
                          </div>
                        )}
                        {on && <div className="outsource-hint">Outsourcing replaces any driver / owner-operator assignment on this trip.</div>}
                      </div>
                    );
                  })}
                  <div className="outsource-summary">
                    <div><span>Order revenue (before tax)</span><strong>{money(subTotal)}</strong></div>
                    <div><span>Carrier cost ({outsourced.length} trip{outsourced.length === 1 ? '' : 's'})</span><strong>{money(carrierCost)}</strong></div>
                    <div className={margin < 0 ? 'neg' : ''}>
                      <span>Margin</span>
                      <strong>{money(margin)}{marginPct != null ? ` (${marginPct.toFixed(1)}%)` : ''}</strong>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ── ACTIONS ───────────────────────────────────────────────── */}
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={handleCancel}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Load'}
          </button>
          {activeTab === 'shipment' && (
            <button type="button" className="btn-primary btn-create-trip" disabled={saving}
              onClick={handleCreateTrip}
              title={trips.length > 0 ? 'Save this order and open the Edit Trip screen' : 'Save this order and open the Create Trip screen'}>
              {saving ? 'Saving…' : (trips.length > 0 ? 'Edit Trip' : 'Create Trip')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
